<template>
	<InvoiceCreatorLayout
		v-if="ready"
		:title="pageTitle"
		@back="navigateBack"
	>
		<template #details>
			<InvoiceHeader
				:businessInfo="businessName"
				:invoiceNumber="form.invoiceNumber"
				:purchaseOrder="form.purchaseOrder"
				:invoiceDate="form.invoiceDate"
				:dueDate="form.dueDate"
				:showDueDate="!isCreditNoteMode"
				:disabled="snapshotLocked"
				@update:invoiceNumber="(v) => (form.invoiceNumber = v)"
				@update:purchaseOrder="(v) => (form.purchaseOrder = v)"
				@update:invoiceDate="onInvoiceDateChange"
				@update:dueDate="(v) => (form.dueDate = v)"
			>
				<template #client>
					<CatalogSearchInput
						use-fixed
						:modelValue="form.clientId"
						:options="clientOptions"
						placeholder="Select client"
						:disabled="snapshotLocked"
						@update:modelValue="onClientSelected"
					/>
				</template>
				<template #building>
					<CatalogSearchInput
						use-fixed
						selection-only
						:modelValue="form.buildingId"
						:options="buildingSelectionOptions"
						:disabled="snapshotLocked || !hasClient"
						:placeholder="!hasClient ? 'Select client first' : clientBuildingCount ? `All buildings (${clientBuildingCount})` : 'No linked buildings'"
						@update:modelValue="onBuildingSelected"
					/>
				</template>
				<template #status>
					<StatusBadgeDropdown
						:current-status="form.invoiceStatus"
						:disabled="snapshotLocked"
						@update:status="(s) => (form.invoiceStatus = s)"
					/>
				</template>
			</InvoiceHeader>
			<div v-if="isCreditNoteMode" class="credit-note-banner">
				<span class="credit-note-banner-title">Credit note mode</span>
				<span class="credit-note-banner-text">{{ creditNoteReferenceText }}</span>
			</div>
		</template>

		<template #items>
			<template v-if="hasClient">
				<InvoiceItemsManager
					class="items-manager"
					:show-building-selector="false"
					:dateRanges="form.invoiceContent.dateRanges"
					:customDates="form.invoiceContent.customDates"
					:buildingOptions="filteredBuildingOptions"
					:clientBuildingCount="clientBuildingCount"
					:allowWithoutBuilding="Boolean(sourceQuotationDocumentId)"
					:serviceOptions="serviceOptions"
					:serviceGroupOptions="serviceGroupOptions"
					:cosOptions="cosOptions"
					:selectedBuildingId="form.buildingId"
					:disabled="snapshotLocked"
					:isFetchingRanges="isFetchingRanges"
					:useQuantityPricingColumns="useQuantityPricingColumns"
					@update:selectedBuildingId="onBuildingSelected"
					@add-date-range="addRange"
					@add-custom-date="addCustomDate"
					@remove-date-range="removeDateRange"
					@remove-custom-date="removeCustomDate"
					@add-item-to-range="addRangeItem"
					@add-item-to-custom="addCustomItemToCustomDate"
					@remove-all-custom-items="removeAllCustomItems"
					@toggle-full-items="toggleFullItems"
					@refresh-range="refreshRange"
				/>

				<div v-if="showInvoiceSummary" class="panel inline-panel no-border-panel">
					<InvoiceNotes v-model="form.notes" :disabled="snapshotLocked" />
					<InvoiceTotals
						:subtotalAmount="summarySubtotalDisplay"
						:vatAmount="summaryVatDisplay"
						:totalAmount="summaryTotalDisplay"
						:vatRate="activeVatRate"
						:vatEnabled="isVatEnabled"
						:disabled="snapshotLocked"
						@update:vatEnabled="onVatEnabledChange"
					/>
				</div>
			</template>
			<div v-else class="panel inline-panel no-border-panel muted-helper no-selection-message">
				Select a client to begin adding invoice items.
			</div>
		</template>

		<template v-if="hasClient && showInvoiceSummary" #footer>
			<button
				v-if="canUpgradeLineItemLayout"
				class="floating-action-btn"
				type="button"
				title="Use Description, Qty, Unit Price and Amount columns for this invoice"
				@click="upgradeLineItemLayout"
			>
				<AppointmentIcon name="table-format" :size="18" />
				<span>Use new item format</span>
			</button>
			<button class="floating-action-btn" type="button" @click="previewInvoice" :disabled="saving">
				<AppointmentIcon name="preview" :size="18" />
				<span>{{ isCreditNoteMode ? 'Preview credit note' : 'Preview invoice' }}</span>
			</button>
			<button class="floating-action-btn" type="button" @click="closePreview" v-if="showPreview">
				<AppointmentIcon name="close" :size="18" />
				<span>Close</span>
			</button>
			<button
				v-if="showSourceUpdated"
				class="floating-action-btn"
				type="button"
				@click="handleFetchUpdatesClick"
				:disabled="saving || isFetchingRanges"
			>
				<AppointmentIcon name="refresh" :size="18" />
				<span>{{ isFetchingRanges ? 'Fetching…' : 'Fetch updates' }}</span>
			</button>
			<button class="floating-action-btn" type="button" @click="downloadPdf" :disabled="saving || pdfGenerating">
				<AppointmentIcon name="download" :size="18" />
				<span>{{ pdfGenerating ? 'Generating…' : 'Download PDF' }}</span>
			</button>
			<button class="floating-action-btn floating-action-btn--primary" type="button" @click="handleSave" :disabled="saveDisabled">
				<AppointmentIcon name="save" :size="18" />
				<span>{{ saving ? 'Saving…' : (isCreditNoteMode ? 'Save credit note' : 'Save invoice') }}</span>
			</button>
			<span v-if="showUnsavedChanges" class="unsaved-pill">Unsaved changes</span>
			<span v-if="showSourceUpdated" class="unsaved-pill">Source data updated</span>
			<div v-if="errorMessage" class="error-inline">{{ errorMessage }}</div>
		</template>
	</InvoiceCreatorLayout>
	<div v-else class="loading-state" ref="scrollRef">Loading invoice…</div>

	<InvoicePreview
		:show-preview="showPreview"
		:invoice-html="previewHtml"
		@close-preview="closePreview"
		@download-pdf="downloadPdf"
	/>

	<div v-if="showFetchUpdatesConfirm" class="modal-overlay" @click="cancelFetchUpdatesConfirm">
		<div class="modal-content" @click.stop>
			<h3 class="modal-title">{{ fetchUpdatesConfirmTitle }}</h3>
			<p class="modal-message">{{ fetchUpdatesConfirmMessage }}</p>
			<ul v-if="fetchUpdatesConfirmDetails.length" class="modal-details">
				<li v-for="(detail, idx) in fetchUpdatesConfirmDetails" :key="idx">
					{{ detail }}
				</li>
			</ul>
			<div class="modal-actions">
				<button
					class="modal-btn cancel"
					type="button"
					@click="cancelFetchUpdatesConfirm"
					:disabled="saving || isFetchingRanges"
				>
					Cancel
				</button>
				<button
					class="modal-btn confirm"
					type="button"
					@click="confirmFetchUpdatesConfirm"
					:disabled="saving || isFetchingRanges"
				>
					Fetch updates
				</button>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { endOfWeek } from 'date-fns';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import { useClientInvoicesStore } from '@/stores/clientInvoices';
import { convertImageToDataUrl } from '@/utils/imageUtils';
import { useTabsStore } from '@/stores/tabs';
import InvoiceCreatorLayout from './components/InvoiceCreatorLayout.vue';
import InvoiceHeader from '@/components/invoices/InvoiceHeader.vue';
import InvoiceItemsManager from '@/components/invoices/InvoiceItemsManager.vue';
import InvoiceNotes from '@/components/invoices/InvoiceNotes.vue';
import InvoiceTotals from '@/components/invoices/InvoiceTotals.vue';
import InvoicePreview from '@/components/invoices/InvoicePreview.vue';
import StatusBadgeDropdown from '@/components/invoices/StatusBadgeDropdown.vue';
import { useJournalAppointmentsStore } from '@/stores/journalAppointments';
import { useJournalPurchasesStore } from '@/stores/journalPurchases';
import { useCatalogStore } from '@/stores/catalog';
import { toBig, useInvoiceCalculations } from '@/composables/useInvoiceCalculations';
import {
	buildBusinessInfo,
	generateInvoiceHtmlWithAutoSpacing,
	deriveInvoiceFilename,
	QUANTITY_PRICING_LINE_ITEM_LAYOUT,
	usesQuantityPricingInvoiceLayout,
} from '@/utils/invoiceDocument';
import CatalogSearchInput from '@/components/CatalogSearchInput.vue';
import { useProjectsStore } from '@/stores/projects';
import { onEvent } from '@/utils/eventBus';
import { waitForClonedDocumentFonts } from '@/utils/clientDocumentTheme';
import { CREDIT_NOTE_PREFIX, INVOICE_CURRENCY, INVOICE_VAT_RATE } from '@/constants/invoiceDefaults';
import {
	isInvoiceDueOnReceipt,
	normalizeInvoicePaymentTerms,
} from '@/utils/invoicePaymentTerms';

import { useMediaStore } from '@/stores/media';


const emit = defineEmits(['back-to-list']);

const props = defineProps({
	listId: {
		type: [String, Number],
		default: null,
	},
	documentId: {
		type: [String, Number],
		default: null,
	},
	invoiceNumber: {
		type: [String, Number],
		default: null,
	},
	draftId: {
		type: [String, Number],
		default: null,
	},
	mode: {
		type: String,
		default: 'invoice',
	},
	sourceDocumentId: {
		type: [String, Number],
		default: null,
	},
	sourceInvoiceNumber: {
		type: [String, Number],
		default: null,
	},
	sourceQuotationDocumentId: {
		type: [String, Number],
		default: null,
	},
	initialInvoice: {
		type: Object,
		default: null,
	},
	tabId: {
		type: String,
		default: null,
	},
});

const clientInvoicesStore = useClientInvoicesStore();
const tabsStore = useTabsStore();
const journalAppointmentsStore = useJournalAppointmentsStore();
const journalPurchasesStore = useJournalPurchasesStore();
const catalogStore = useCatalogStore();
const projectsStore = useProjectsStore();
const mediaStore = useMediaStore();
const { proxy } = getCurrentInstance() || {};
const toast = proxy?.$toast;
const logError = proxy?.$logError;
const {
	calculateAmount,
	calculateItemAmount,
	calculateSummary,
	clearErrors,
	hasErrors,
} = useInvoiceCalculations();
const scrollRef = ref(null);
const errorMessage = ref('');
const isFetchingRanges = ref(false);
const snapshotLoading = ref(false);
const snapshotLocked = ref(false);
const pdfGenerating = ref(false);
const pristineSnapshot = ref('');
const hasUnsavedChanges = ref(false);
const trackingReady = ref(false);
const showPreview = ref(false);
const previewHtml = ref('');
const showFetchUpdatesConfirm = ref(false);
const fetchUpdatesConfirmState = ref(null);
const projectInfoData = computed(() => {
	return buildBusinessInfo(projectsStore.activeProject);
});

const businessName = computed(() => {
	return projectInfoData.value.businessName || projectsStore.activeProject?.name || '';
});

const projectBankDetails = computed(
	() => projectInfoData.value.bankDetails || '',
);
const clientOptions = computed(() => catalogStore.clientOptions || []);
const serviceOptions = computed(() => {
	const catalog = catalogStore.catalogByName?.('Services');
	return (catalog && catalogStore.itemsByCatalogId?.[catalog.id]) || [];
});
const serviceGroupOptions = computed(() => catalogStore.serviceGroupOptions || []);
const cosOptions = computed(() => catalogStore.optionsByCatalogName?.('COS') || []);
const buildingCatalog = computed(() => catalogStore.catalogByName?.("Clients’ Buildings"));
const allBuildingOptions = computed(() =>
	buildingCatalog.value?.id ? catalogStore.itemsByCatalogId?.[buildingCatalog.value.id] || [] : [],
);
const filteredBuildingOptions = computed(() => {
	if (!form.clientId) return allBuildingOptions.value;
	const clientItem = catalogStore.itemsByCatalogId
		? Object.values(catalogStore.itemsByCatalogId).flat().find((it) => String(it.id) === String(form.clientId))
		: null;
	const allowedRaw = clientItem?.buildings || clientItem?.data?.buildings;
	const allowed = Array.isArray(allowedRaw) ? allowedRaw : [];
	if (!allowed.length) return [];
	return allBuildingOptions.value.filter((b) => allowed.includes(b.id));
});

const isBuildingAllowedForClient = (buildingId) =>
	filteredBuildingOptions.value.some((option) => String(option.id) === String(buildingId));
const clientBuildingIds = computed(() => {
	if (!form.clientId) return [];
	return (filteredBuildingOptions.value || []).map((b) => b.id).filter(Boolean);
});
const clientBuildingCount = computed(() => clientBuildingIds.value.length);
const buildingSelectionOptions = computed(() => clientBuildingCount.value
	? [{ id: null, name: `All buildings (${clientBuildingCount.value})` }, ...filteredBuildingOptions.value]
	: [],
);

const normalizeId = (value) => {
	if (value === null || value === undefined) return null;
	return String(value);
};

const normalizeEditorMode = (value) => {
	const normalized = String(value || '').trim().toLowerCase();
	return normalized === 'credit-note' ? 'credit-note' : 'invoice';
};

const cleanClientSnapshot = (snapshot = {}) => ({
	name: snapshot.name || '',
	buildingName: snapshot.buildingName || '',
	address: snapshot.address || '',
	customerId: snapshot.customerId || '',
	terms: normalizeInvoicePaymentTerms(snapshot.terms),
	email: snapshot.email || '',
	telephone: snapshot.telephone || snapshot.phone || snapshot.phoneNumber || '',
});

const findCatalogItemById = async (id) => {
	if (!id) return null;
	if (!catalogStore.initialized) {
		try {
			await catalogStore.bootstrap?.();
		} catch (error) {
			logError(error);
			return null;
		}
	}
	// Use efficient O(1) indexed lookup
	return catalogStore.getItemById?.(id) ?? null;
};

const onClientSelected = async (clientId) => {
	form.clientId = clientId;
	if (!clientId || (form.buildingId && !isBuildingAllowedForClient(form.buildingId))) {
		form.buildingId = null;
		form.invoiceContent.buildingScope = 'all';
		if (!snapshotLocked.value) {
			form.clientSnapshot.buildingName = '';
		}
	}
	if (!snapshotLocked.value) {
		try {
			await refreshSnapshotFromCatalog({ preserveExisting: false });
		} catch (error) {
			logError(error);
		}
	}
	computeDueDateFromTerms();
};

const onBuildingSelected = (buildingId) => {
	form.buildingId = buildingId || null;
	form.invoiceContent.buildingScope = form.buildingId ? 'single' : 'all';
	const match = filteredBuildingOptions.value.find((b) => String(b.id) === String(buildingId));
	if (!snapshotLocked.value) {
		form.clientSnapshot.buildingName = match?.name || '';
	}
	// Auto add an empty range when selecting a building if none exist
	if (form.buildingId && form.invoiceContent.dateRanges.length === 0) {
		addRange();
	}
};

const onVatEnabledChange = (enabled) => {
	if (snapshotLocked.value) return;
	form.invoiceContent.vatRate = enabled ? INVOICE_VAT_RATE : ZERO_VAT_RATE;
	ensureAmounts();
};

const toNumberOrNull = (value) => {
	if (value === null || value === undefined || value === '') return null;
	const raw = String(value).trim();
	try {
		const numeric = Number(toBig(raw));
		return Number.isFinite(numeric) ? numeric : null;
	} catch (error) {
		return null;
	}
};

const ZERO_VAT_RATE = 0;
const normalizeVatRate = (value) => {
	const numeric = toNumberOrNull(value);
	if (numeric === ZERO_VAT_RATE) return ZERO_VAT_RATE;
	return INVOICE_VAT_RATE;
};

const PURCHASE_MARKUP_RATE = 0.15;
const PURCHASE_MARKUP_MULTIPLIER = 1 + PURCHASE_MARKUP_RATE;

const applyPurchaseMarkup = (value) => {
	const numeric = toNumberOrNull(value);
	if (numeric === null) return 0;
	return Number(toBig(numeric).times(PURCHASE_MARKUP_MULTIPLIER).toFixed(2));
};

// A date range can bill its purchases at gross, without the 15% (sources.purchases.markup).
const purchaseInvoiceAmount = (value, markup = true) => {
	if (markup) return applyPurchaseMarkup(value);
	const numeric = toNumberOrNull(value);
	if (numeric === null) return 0;
	return Number(toBig(numeric).toFixed(2));
};

const normalizeIsoDate = (value) => {
	if (!value) return null;
	const str = String(value);
	return str.length >= 10 ? str.slice(0, 10) : str;
};

const normalizeSourceId = (value) => {
	if (value === null || value === undefined || value === '') return null;
	return String(value);
};

const normalizeOptionalText = (value) => String(value ?? '').trim();

const normalizeServiceGroupIds = (value) => {
	if (Array.isArray(value)) {
		return value.filter((id) => id !== null && id !== undefined && id !== '');
	}
	if (value === null || value === undefined || value === '') return [];
	return [value];
};

const normalizeIdList = (value) => normalizeServiceGroupIds(value);

const normalizeRangeSources = (range = {}) => {
	const sources = range?.sources && typeof range.sources === 'object' ? range.sources : null;
	if (sources) {
		const appointments = sources.appointments || {};
		const purchases = sources.purchases || {};
		const appointmentGroups = normalizeIdList(appointments.serviceGroupIds);
		const purchaseCos = normalizeIdList(purchases.cosIds);
		return {
			appointments: {
				enabled: Boolean(appointments.enabled ?? appointments.all ?? appointmentGroups.length),
				all: Boolean(appointments.all),
				serviceGroupIds: appointmentGroups,
			},
			purchases: {
				enabled: Boolean(purchases.enabled ?? purchases.all ?? purchaseCos.length),
				all: Boolean(purchases.all),
				cosIds: purchaseCos,
				markup: purchases.markup !== false,
			},
		};
	}

	const legacyType = range?.sourceType || 'appointments';
	if (legacyType === 'purchases') {
		const cosIds = normalizeIdList(range?.cosId);
		return {
			appointments: { enabled: false, all: false, serviceGroupIds: [] },
			purchases: {
				enabled: true,
				all: !cosIds.length,
				cosIds,
				markup: true,
			},
		};
	}
	if (legacyType === 'custom') {
		return {
			appointments: { enabled: false, all: false, serviceGroupIds: [] },
			purchases: { enabled: false, all: false, cosIds: [], markup: true },
		};
	}
	const serviceGroupIds = normalizeIdList(range?.serviceGroupIds ?? range?.serviceGroupId);
	return {
		appointments: {
			enabled: true,
			all: !serviceGroupIds.length,
			serviceGroupIds,
		},
		purchases: { enabled: false, all: false, cosIds: [], markup: true },
	};
};

const deriveLegacySourceType = (sources = {}, fallback = 'appointments') => {
	const appointmentsEnabled = Boolean(sources?.appointments?.enabled);
	const purchasesEnabled = Boolean(sources?.purchases?.enabled);
	if (appointmentsEnabled && purchasesEnabled) return 'mixed';
	if (purchasesEnabled) return 'purchases';
	if (appointmentsEnabled) return 'appointments';
	return fallback;
};

const getRangeServiceGroupIds = (range) =>
	normalizeRangeSources(range).appointments.serviceGroupIds;

const getRangeCosIds = (range) => normalizeRangeSources(range).purchases.cosIds;

const hasAppointmentsSource = (range) => normalizeRangeSources(range).appointments.enabled;
const hasPurchasesSource = (range) => normalizeRangeSources(range).purchases.enabled;

const generateInvoiceLineId = () => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	return `line_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
};

const areNumbersEqual = (a, b) => {
	const left = toNumberOrNull(a);
	const right = toNumberOrNull(b);
	if (left === null && right === null) return true;
	return left === right;
};

const ensureInvoiceLineSources = (line) => {
	if (!line || typeof line !== 'object') return;
	if (!line.lineId) {
		line.lineId = generateInvoiceLineId();
	}
	if (line.sourceFlatRef === undefined) {
		line.sourceFlatRef = line.flatRef || '';
	}
	if (line.sourceHours === undefined) {
		line.sourceHours = toNumberOrNull(line.hours);
	}
	if (line.sourceRate === undefined) {
		line.sourceRate = toNumberOrNull(line.rate);
	}
	if (line.sourceDirectAmount === undefined) {
		line.sourceDirectAmount = toNumberOrNull(line.directAmount);
	}
	if (line.sourceDescription === undefined) {
		line.sourceDescription = line.description || '';
	}
};

const createManualInvoiceItem = () => {
	const item = {
		description: '',
		flatRef: '',
		hours: '',
		rate: '',
		amount: '',
		amountMode: 'direct',
		directAmount: '',
		manual: true,
	};
	ensureInvoiceLineSources(item);
	return item;
};

const formatDateInputValue = (date) => {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
	const yyyy = date.getFullYear();
	const mm = String(date.getMonth() + 1).padStart(2, '0');
	const dd = String(date.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
};

const parseLocalDateValue = (value) => {
	if (!value) return null;
	const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (!match) return null;
	const year = Number(match[1]);
	const month = Number(match[2]);
	const day = Number(match[3]);
	const date = new Date(year, month - 1, day);
	return Number.isNaN(date.getTime()) ? null : date;
};

const addDays = (dateIso, days = 0) => {
	if (!dateIso) return todayIso(days);
	const d = parseLocalDateValue(dateIso);
	if (!d) return todayIso(days);
	d.setDate(d.getDate() + days);
	return formatDateInputValue(d);
};

const documentId = computed(() => normalizeId(props.documentId));
const propEditorMode = computed(() => normalizeEditorMode(props.mode));
const sourceCreditDocumentId = computed(() => normalizeId(props.sourceDocumentId));
const sourceCreditInvoiceNumber = computed(() =>
	props.sourceInvoiceNumber === null || props.sourceInvoiceNumber === undefined
		? ''
		: String(props.sourceInvoiceNumber),
);
const initialInvoiceNumber = computed(() => (props.invoiceNumber ? String(props.invoiceNumber) : ''));
const listId = computed(
	() => normalizeId(props.listId) || normalizeId(clientInvoicesStore.activeListId),
);
const draftId = computed(() => normalizeId(props.draftId));
const hasClient = computed(() => Boolean(form.clientId));
const buildingScope = computed(() => (form.buildingId ? 'single' : 'all'));
const activeBuildingIds = computed(() => {
	if (form.buildingId) {
		return [form.buildingId];
	}
	if (!form.clientId) {
		return [];
	}
	return clientBuildingIds.value;
});
const summarySubtotalDisplay = computed(() => {
	const subtotal = toNumberOrNull(form.invoiceContent.summary.subtotal);
	if (subtotal !== null) return subtotal;
	const fallback = toNumberOrNull(form.totalAmount);
	return fallback ?? 0;
});
const summaryVatDisplay = computed(() => {
	const vat = toNumberOrNull(form.invoiceContent.summary.vat);
	return vat ?? 0;
});
const summaryTotalDisplay = computed(() => {
	const total = toNumberOrNull(form.invoiceContent.summary.total);
	if (total !== null) return total;
	const fallback = toNumberOrNull(form.totalAmount);
	return fallback ?? 0;
});
const activeVatRate = computed(() => normalizeVatRate(form.invoiceContent.vatRate));
const isVatEnabled = computed(() => activeVatRate.value > ZERO_VAT_RATE);
const hasInvoiceItems = computed(() => {
	const rangeItems = (form.invoiceContent?.dateRanges || []).some(
		(range) => Array.isArray(range.items) && range.items.length > 0,
	);
	const customItems = (form.invoiceContent?.customDates || []).some(
		(entry) => Array.isArray(entry.items) && entry.items.length > 0,
	);
	return rangeItems || customItems;
});
const showInvoiceSummary = computed(() => {
	if (!hasInvoiceItems.value) return false;
	if (isCreditNoteMode.value) return true;
	return summaryTotalDisplay.value > 0;
});
const ready = ref(false);
	const saving = ref(false);
	const bootstrapped = ref(false);
	const seedingInvoiceNumber = ref(false);
	const hydratingContent = ref(false);
	const loadedDocumentMode = ref(null);
	const isCreditNoteMode = computed(() => {
		if (propEditorMode.value === 'credit-note') return true;
		return normalizeEditorMode(loadedDocumentMode.value) === 'credit-note';
	});
	const isPersisted = computed(() => Boolean(documentId.value));
	// Auto-sync catalog/appointments only before first save.
	const autoSyncEnabled = computed(
		() => !isPersisted.value && !snapshotLocked.value && !isCreditNoteMode.value,
	);
	const pendingCatalogUpdate = ref(false);
	const pendingSourceCatalogUpdate = ref(false);
	const pendingAppointmentsUpdate = ref(false);
	const pendingPurchasesUpdate = ref(false);
	const showSourceUpdated = computed(
		() =>
			isPersisted.value &&
			!snapshotLocked.value &&
			(
				pendingCatalogUpdate.value ||
				pendingSourceCatalogUpdate.value ||
				pendingAppointmentsUpdate.value ||
				pendingPurchasesUpdate.value
			),
	);
	const suppressAutoRefreshOnce = ref(false);
	const suppressSnapshotRefreshOnce = ref(false);

const todayIso = (offsetDays = 0) => {
	const base = new Date();
	base.setDate(base.getDate() + offsetDays);
	return formatDateInputValue(base);
};

const serializeLine = (line = {}) => {
	const amountMode = (line.amountMode || '').toLowerCase() === 'direct' ? 'direct' : 'calculated';
	return {
		description: line.description || '',
		flatRef: line.flatRef || '',
		hours: toNumberOrNull(line.hours),
		rate: toNumberOrNull(line.rate),
		amount: toNumberOrNull(line.amount),
		directAmount: toNumberOrNull(line.directAmount),
		amountMode,
		manual: !!line.manual,
		sourceAppointmentIds: line.sourceAppointmentIds || [],
		sourceId: line.sourceId || null,
		sourceType: line.sourceType || '',
		serviceId: line.serviceId ?? null,
		serviceName: line.serviceName || '',
		buildingId: line.buildingId ?? null,
		buildingName: line.buildingName || '',
		vendorId: line.vendorId ?? null,
		vendorName: line.vendorName || '',
	};
};

const serializeInvoiceContent = (content = {}) => {
	const mapCollection = (collection = []) => (Array.isArray(collection) ? collection.map(serializeLine) : []);
	return {
		currency: INVOICE_CURRENCY,
		vatRate: normalizeVatRate(content.vatRate),
		buildingScope: content.buildingScope || 'all',
		dateRanges: Array.isArray(content.dateRanges)
			? content.dateRanges.map((range) => {
					const sources = normalizeRangeSources(range);
					return {
						sources,
						dateFrom: range?.dateFrom || '',
						dateTo: range?.dateTo || '',
						sourceType: deriveLegacySourceType(sources, range?.sourceType || 'appointments'),
						serviceGroupIds: sources.appointments.serviceGroupIds,
						serviceGroupId: sources.appointments.serviceGroupIds[0] ?? null,
						cosId: sources.purchases.cosIds[0] ?? null,
						excludedPurchaseIds: Array.isArray(range?.excludedPurchaseIds) ? range.excludedPurchaseIds : [],
						excludedAppointmentIds: Array.isArray(range?.excludedAppointmentIds)
							? range.excludedAppointmentIds
							: [],
						includedPurchaseIds: Array.isArray(range?.includedPurchaseIds)
							? range.includedPurchaseIds
							: [],
						items: mapCollection(range?.items),
					};
			  })
			: [],
		customDates: Array.isArray(content.customDates)
			? content.customDates.map((entry) => ({
					date: entry?.date || '',
					items: mapCollection(entry?.items),
			  }))
			: [],
		summary: {
			subtotal: toNumberOrNull(content.summary?.subtotal),
			vat: toNumberOrNull(content.summary?.vat),
			total: toNumberOrNull(content.summary?.total),
		},
	};
};

const serializeFormSnapshot = () =>
	JSON.stringify({
		invoiceNumber: form.invoiceNumber || '',
		invoiceStatus: form.invoiceStatus || 'unpaid',
		invoiceDate: form.invoiceDate || '',
		dueDate: form.dueDate || '',
		purchaseOrder: normalizeOptionalText(form.purchaseOrder),
		paidDate: form.paidDate || '',
		totalAmount: toNumberOrNull(form.totalAmount),
		notes: form.notes || '',
		clientId: form.clientId || null,
		buildingId: form.buildingId || null,
		clientSnapshot: {
			name: form.clientSnapshot.name || '',
			buildingName: form.clientSnapshot.buildingName || '',
			address: form.clientSnapshot.address || '',
			customerId: form.clientSnapshot.customerId || '',
			terms: form.clientSnapshot.terms || '',
			email: form.clientSnapshot.email || '',
			telephone: form.clientSnapshot.telephone || '',
		},
		metadata: {
			lineItemLayout: form.metadata?.lineItemLayout || '',
		},
		invoiceContent: serializeInvoiceContent(form.invoiceContent || {}),
	});

const markPristineSnapshot = ({ enableTracking = false } = {}) => {
	pristineSnapshot.value = serializeFormSnapshot();
	hasUnsavedChanges.value = false;
	if (enableTracking) {
		trackingReady.value = true;
	}
};

const form = reactive({
	invoiceNumber: '',
	invoiceStatus: 'unpaid',
	invoiceDate: '',
	dueDate: '',
	purchaseOrder: '',
	paidDate: '',
	totalAmount: '',
	notes: '',
	clientId: null,
	buildingId: null,
	clientSnapshot: {
		name: '',
		buildingName: '',
		address: '',
		customerId: '',
		terms: '',
		email: '',
		telephone: '',
	},
	metadata: {
		snapshotFrozen: false,
	},
	invoiceContent: {
		currency: INVOICE_CURRENCY,
		vatRate: INVOICE_VAT_RATE,
		buildingScope: 'all',
		dateRanges: [],
		customDates: [],
		summary: {
			subtotal: '',
			vat: '',
			total: '',
		},
	},
});

const useQuantityPricingColumns = computed(() =>
	usesQuantityPricingInvoiceLayout(
		form.invoiceDate,
		form.metadata?.lineItemLayout,
	),
);

const canUpgradeLineItemLayout = computed(() =>
	form.invoiceStatus !== 'paid'
	&& !snapshotLocked.value
	&& !isCreditNoteMode.value
	&& !useQuantityPricingColumns.value,
);

const upgradeLineItemLayout = () => {
	if (form.invoiceStatus === 'paid' || snapshotLocked.value || isCreditNoteMode.value) return;
	form.metadata = {
		...(form.metadata || {}),
		lineItemLayout: QUANTITY_PRICING_LINE_ITEM_LAYOUT,
	};
};

const currentList = computed(() =>
	clientInvoicesStore.lists.find((entry) => entry.id === listId.value) || null,
);
const creditNoteReferenceText = computed(() => {
	if (!isCreditNoteMode.value) return '';
	const sourceNumber =
		form.metadata?.creditNote?.sourceInvoiceNumber || sourceCreditInvoiceNumber.value;
	if (sourceNumber) {
		return `Linked to invoice #${sourceNumber}`;
	}
	const sourceDocId =
		form.metadata?.creditNote?.sourceInvoiceDocumentId || sourceCreditDocumentId.value;
	if (sourceDocId) {
		return `Linked to source invoice ${sourceDocId}`;
	}
	return 'Use negative line amounts and include a reason for issue.';
});
const myTabId = computed(() => {
	if (props.tabId) return props.tabId;
	// Fallback: try to locate this tab by section + itemId (documentId or draftId)
	const targetId = documentId.value || draftId.value;
	if (!targetId) return null;
	const match = tabsStore.tabs.find(
		(tab) => tab.section === 'invoice-editor' && String(tab.itemId) === String(targetId),
	);
	return match?.id || null;
});
const isActiveTab = computed(() => {
	const tabId = myTabId.value;
	if (!tabId) return true;
	return tabsStore.activeTabId === tabId;
});
const applyTabUpdates = (updates = {}) => {
	const targetId = myTabId.value || tabsStore.activeTabId;
	if (!targetId) return;
	tabsStore.updateTab(targetId, updates);
};
const pendingToasts = ref([]);
const pendingToastSet = new Set();
/**
 * Generates a unique invoice number with retry mechanism to reduce race conditions.
 * Uses a random suffix if multiple attempts yield the same number.
 */
const ensureInvoiceNumberSeeded = async () => {
	if (isCreditNoteMode.value) return;
	if (documentId.value || form.invoiceNumber || seedingInvoiceNumber.value) return;
	if (!listId.value) return;
	seedingInvoiceNumber.value = true;
	const MAX_RETRIES = 3;
	let attempts = 0;
	try {
		while (attempts < MAX_RETRIES) {
			const nextNum = await clientInvoicesStore.fetchNextInvoiceNumber(listId.value);
			if (!documentId.value && !form.invoiceNumber && nextNum) {
				// Add small random delay to reduce race condition window
				if (attempts > 0) {
					await new Promise((r) => setTimeout(r, Math.random() * 100 + 50));
					// Refetch after delay to get latest
					const refreshedNum = await clientInvoicesStore.fetchNextInvoiceNumber(listId.value);
					if (refreshedNum && refreshedNum !== nextNum) {
						form.invoiceNumber = refreshedNum;
						break;
					}
				}
				form.invoiceNumber = nextNum;
				break;
			}
			attempts++;
		}
	} finally {
		seedingInvoiceNumber.value = false;
	}
};

const buildEditorTitle = (invoiceData = null) => {
	const normalizedDocumentId = normalizeId(invoiceData?.documentId ?? documentId.value);
	const invoiceNumber = invoiceData?.invoiceNumber ?? form.invoiceNumber;
	const explicitMode = normalizeEditorMode(invoiceData?.metadata?.documentType);
	const currentMode = explicitMode === 'credit-note' || isCreditNoteMode.value ? 'credit-note' : 'invoice';
	const label = currentMode === 'credit-note' ? 'credit note' : 'invoice';
	if (normalizedDocumentId) {
		const number = invoiceNumber || normalizedDocumentId;
		return `Edit ${label} #${number}`;
	}
	return currentMode === 'credit-note' ? 'New credit note' : 'New client invoice';
};

const normalizeCompareText = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();
const normalizeCatalogName = (value) => String(value || '').trim().toLowerCase();

const isServiceCatalogEvent = (payload = {}) => {
	const name = normalizeCatalogName(payload?.catalogName);
	if (name) return name === 'services';
	const serviceCatalog = catalogStore.catalogByName?.('Services');
	if (!serviceCatalog?.id || !payload?.catalogId) return false;
	return String(payload.catalogId) === String(serviceCatalog.id);
};

const isServiceGroupEvent = (payload = {}) => {
	if (payload?.serviceGroupsChanged) return true;
	const name = normalizeCatalogName(payload?.catalogName);
	return ['service groups', 'services groups', 'service-groups', 'services-groups'].includes(name);
};

const resolveServiceGroupLabel = (id) => {
	if (id === null || id === undefined || id === '') return '';
	const match = serviceGroupOptions.value.find((option) => String(option?.id) === String(id));
	return (
		match?.name ||
		match?.label ||
		match?.attributes?.name ||
		match?.attributes?.label ||
		String(id)
	);
};

const formatServiceGroupSelection = (ids = []) => {
	if (!ids.length) return '';
	const labels = ids.map(resolveServiceGroupLabel).filter(Boolean);
	if (!labels.length) return ids.join(', ');
	return labels.join(', ');
};

const resolveCosLabel = (id) => {
	if (id === null || id === undefined || id === '') return '';
	const match = cosOptions.value.find((option) => String(option?.id) === String(id));
	return (
		match?.name ||
		match?.label ||
		match?.attributes?.name ||
		match?.attributes?.label ||
		String(id)
	);
};

const formatCosSelection = (ids = []) => {
	if (!ids.length) return '';
	const labels = ids.map(resolveCosLabel).filter(Boolean);
	if (!labels.length) return ids.join(', ');
	return labels.join(', ');
};

const describeRangeForConfirm = (range) => {
	if (!range) return 'Unknown range';
	const dates = range.dateFrom && range.dateTo ? `${range.dateFrom} → ${range.dateTo}` : 'Incomplete range';
	const sources = normalizeRangeSources(range);
	const labels = [];
	if (sources.appointments.enabled) {
		if (sources.appointments.all || !sources.appointments.serviceGroupIds.length) {
			labels.push('Appointments');
		} else {
			labels.push(
				`Appointments (Service group${sources.appointments.serviceGroupIds.length > 1 ? 's' : ''}: ${formatServiceGroupSelection(
					sources.appointments.serviceGroupIds,
				)})`,
			);
		}
	}
	if (sources.purchases.enabled) {
		if (sources.purchases.all || !sources.purchases.cosIds.length) {
			labels.push('Purchases');
		} else {
			labels.push(
				`Purchases (COS${sources.purchases.cosIds.length > 1 ? 's' : ''}: ${formatCosSelection(
					sources.purchases.cosIds,
				)})`,
			);
		}
	}
	if (!labels.length) {
		labels.push('No sources selected');
	}
	return `${dates} (${labels.join(' + ')})`;
};

const getEditedFieldsForJournalDerivedLine = (line) => {
	if (!line || typeof line !== 'object') return [];
	if (line.manual) return [];

	const fields = [];
	const mode = (line.amountMode || '').toLowerCase() === 'direct' ? 'direct' : 'calculated';

	if (normalizeCompareText(line.description) !== normalizeCompareText(line.sourceDescription)) {
		fields.push('description');
	}
	if (normalizeCompareText(line.flatRef) !== normalizeCompareText(line.sourceFlatRef)) {
		fields.push('flat ref');
	}

	if (mode === 'direct') {
		if (!areNumbersEqual(line.directAmount, line.sourceDirectAmount)) {
			fields.push('amount');
		}
	} else {
		if (!areNumbersEqual(line.hours, line.sourceHours)) {
			fields.push('hours');
		}
		if (!areNumbersEqual(line.rate, line.sourceRate)) {
			fields.push('rate');
		}
	}

	return fields;
};

const computeDueDateForTerms = (termsRaw, invoiceDate) => {
	if (!termsRaw || !invoiceDate) return null;
	const terms = normalizeInvoicePaymentTerms(termsRaw);
	if (isInvoiceDueOnReceipt(terms)) return invoiceDate;
	const num = Number(terms);
	if (Number.isNaN(num)) return null;
	return addDays(invoiceDate, num);
};

const toCreditNoteAmount = (value) => {
	const numeric = toNumberOrNull(value);
	if (numeric === null) return '';
	return Number((-Math.abs(numeric)).toFixed(2));
};

const sanitizeDocumentNumberPart = (value) =>
	String(value || '')
		.replace(/[^a-zA-Z0-9-]/g, '')
		.trim();

const buildCreditNoteNumber = (nextInvoiceNumber = '', sourceNumber = '', sourceDocumentId = '') => {
	const nextPart = String(nextInvoiceNumber || '').trim();
	const sourcePart =
		sanitizeDocumentNumberPart(sourceNumber) ||
		sanitizeDocumentNumberPart(sourceDocumentId) ||
		'UNKNOWN';
	if (!nextPart) {
		return `${CREDIT_NOTE_PREFIX}UNKNOWN/${sourcePart}`;
	}
	return `${CREDIT_NOTE_PREFIX}${nextPart}/${sourcePart}`;
};

const ensureUniqueCreditNoteNumber = async (
	sourceNumber = '',
	sourceDocumentId = '',
	excludeDocumentId = null,
) => {
	const fallbackNext = (await clientInvoicesStore.fetchNextInvoiceNumber(listId.value)) || '1';
	const fallback = buildCreditNoteNumber(fallbackNext, sourceNumber, sourceDocumentId);
	try {
		if (typeof clientInvoicesStore.generateUniqueCreditNoteNumber !== 'function') {
			return fallback;
		}
		return await clientInvoicesStore.generateUniqueCreditNoteNumber({
			listId: listId.value,
			sourceInvoiceNumber: sourceNumber,
			sourceDocumentId,
			excludeDocumentId,
		});
	} catch (error) {
		logError(error);
		return fallback;
	}
};

const normalizeLineForCreditNote = (item) => {
	if (!item || typeof item !== 'object') return;
	const mode = (item.amountMode || '').toLowerCase() === 'direct' ? 'direct' : 'calculated';
	if (mode === 'direct') {
		const amount = toNumberOrNull(item.directAmount ?? item.amount);
		if (amount === null) return;
		item.directAmount = Number((-Math.abs(amount)).toFixed(2));
		return;
	}
	const rate = toNumberOrNull(item.rate);
	if (rate !== null) {
		item.rate = Number((-Math.abs(rate)).toFixed(2));
	}
};

const resolveLineAmount = (line = {}) => {
	const amountMode = (line.amountMode || '').toLowerCase() === 'direct' ? 'direct' : 'calculated';
	if (amountMode === 'direct') {
		return toNumberOrNull(line.directAmount ?? line.amount);
	}
	const hours = toNumberOrNull(line.hours);
	const rate = toNumberOrNull(line.rate);
	if (hours !== null && rate !== null) {
		return Number(toBig(hours).times(rate).toFixed(2));
	}
	return toNumberOrNull(line.amount);
};

const mapLineToCreditNote = (line = {}) => {
	const amount = resolveLineAmount(line);
	const creditAmount = toCreditNoteAmount(amount);
	const mapped = {
		...line,
		amountMode: 'direct',
		hours: '',
		rate: '',
		directAmount: creditAmount,
		amount: creditAmount,
	};
	ensureInvoiceLineSources(mapped);
	return mapped;
};

const cloneRangeForCreditNote = (range = {}) => {
	const sources = normalizeRangeSources(range);
	const items = Array.isArray(range.items) ? range.items.map(mapLineToCreditNote) : [];
	return {
		dateFrom: range?.dateFrom || '',
		dateTo: range?.dateTo || '',
		sources,
		sourceType: deriveLegacySourceType(sources, range?.sourceType || 'appointments'),
		serviceGroupIds: sources.appointments.serviceGroupIds,
		serviceGroupId: sources.appointments.serviceGroupIds[0] ?? null,
		cosId: sources.purchases.cosIds[0] ?? null,
		excludedPurchaseIds: normalizeIdList(range?.excludedPurchaseIds),
		excludedAppointmentIds: normalizeIdList(range?.excludedAppointmentIds),
		includedPurchaseIds: normalizeIdList(range?.includedPurchaseIds),
		items,
		fullItems: [],
		excludedPurchases: [],
		showFullItems: false,
		loadingFullItems: false,
	};
};

const cloneCustomDateForCreditNote = (entry = {}) => {
	const items = Array.isArray(entry.items) ? entry.items.map(mapLineToCreditNote) : [];
	return {
		date: entry?.date || '',
		items,
	};
};

const applyCreditNoteFromSourceInvoice = async (sourceInvoice) => {
	if (!sourceInvoice) return;
	const sourceNumber = sourceInvoice.invoiceNumber ? String(sourceInvoice.invoiceNumber) : '';
	const sourceDocument = normalizeId(sourceInvoice.documentId);
	const sourceRefLine = sourceNumber
		? `Credit note for invoice #${sourceNumber}`
		: sourceDocument
			? `Credit note for invoice ${sourceDocument}`
			: 'Credit note';
	const reasonLine = 'Reason: ';
	const sourceNotes = String(sourceInvoice.notes || '').trim();

	form.invoiceNumber = await ensureUniqueCreditNoteNumber(sourceNumber, sourceDocument);
	form.invoiceStatus = 'unpaid';
	form.invoiceDate = todayIso();
	form.dueDate = '';
	form.purchaseOrder = normalizeOptionalText(sourceInvoice.purchaseOrder);
	form.paidDate = '';
	form.totalAmount = '';
	form.notes = sourceNotes ? `${sourceRefLine}\n${reasonLine}\n${sourceNotes}` : `${sourceRefLine}\n${reasonLine}`;
	form.clientId = sourceInvoice.client?.id || null;
	form.buildingId = sourceInvoice.building?.id || null;
	form.clientSnapshot = cleanClientSnapshot(sourceInvoice.clientSnapshot || {});
	if (!form.buildingId) {
		form.clientSnapshot.buildingName = '';
	}

	const sourceContent = sourceInvoice.invoiceContent || {};
	form.invoiceContent = {
		currency: INVOICE_CURRENCY,
		vatRate: normalizeVatRate(sourceContent.vatRate),
		buildingScope: form.buildingId ? 'single' : 'all',
		dateRanges: Array.isArray(sourceContent.dateRanges)
			? sourceContent.dateRanges.map(cloneRangeForCreditNote)
			: [],
		customDates: Array.isArray(sourceContent.customDates)
			? sourceContent.customDates.map(cloneCustomDateForCreditNote)
			: [],
		summary: {
			subtotal: '',
			vat: '',
			total: '',
		},
	};
	form.metadata = {
		...(sourceInvoice.metadata || {}),
		snapshotFrozen: false,
		documentType: 'credit-note',
		creditNote: {
			...(sourceInvoice.metadata?.creditNote || {}),
			sourceInvoiceDocumentId: sourceDocument || null,
			sourceInvoiceNumber: sourceNumber || null,
			sourceInvoiceDate: normalizeIsoDate(sourceInvoice.invoiceDate),
		},
	};
	loadedDocumentMode.value = 'credit-note';
	ensureAmounts();
};

const loadSourceInvoiceForCreditNote = async () => {
	if (!sourceCreditDocumentId.value) return false;
	hydratingContent.value = true;
	try {
		const sourceInvoice = await clientInvoicesStore.fetchInvoiceById(sourceCreditDocumentId.value);
		if (!sourceInvoice) return false;
		await applyCreditNoteFromSourceInvoice(sourceInvoice);
		return true;
	} catch (error) {
		logError(error);
		errorMessage.value = error?.message || 'Failed to load source invoice for credit note.';
		return false;
	} finally {
		hydratingContent.value = false;
	}
};

	const analyzeFetchUpdatesImpact = () => {
		const willRefreshCatalog = Boolean(pendingCatalogUpdate.value);
		const willRefreshRanges = Boolean(
			pendingAppointmentsUpdate.value ||
				pendingPurchasesUpdate.value ||
				pendingSourceCatalogUpdate.value,
		);

	const rangesToRefresh = [];
	if (willRefreshRanges && activeBuildingIds.value.length) {
			(form.invoiceContent?.dateRanges || []).forEach((range, idx) => {
				if (range?.dateFrom && range?.dateTo && hasAnySourcesSelected(normalizeRangeSources(range))) {
					rangesToRefresh.push({ range, idx });
				}
			});
		}

	const editedFieldCounts = {};
	const editedRanges = new Map(); // idx => { label, count }
	let editedLinesCount = 0;

	rangesToRefresh.forEach(({ range, idx }) => {
		(range.items || []).forEach((item) => {
			const editedFields = getEditedFieldsForJournalDerivedLine(item);
			if (!editedFields.length) return;
			editedLinesCount += 1;
			editedFields.forEach((field) => {
				editedFieldCounts[field] = (editedFieldCounts[field] || 0) + 1;
			});
			if (!editedRanges.has(idx)) {
				editedRanges.set(idx, { label: describeRangeForConfirm(range), count: 0 });
			}
			editedRanges.get(idx).count += 1;
		});
	});

	const editedRangesSummary = Array.from(editedRanges.values()).sort((a, b) => b.count - a.count);
	const editedFieldsSummary = Object.keys(editedFieldCounts).sort((a, b) => {
		return (editedFieldCounts[b] || 0) - (editedFieldCounts[a] || 0);
	});

	const dueDateImpact = (() => {
		if (isCreditNoteMode.value) return null;
		if (!willRefreshCatalog) return null;
		const clientItem = catalogStore.getItemById?.(form.clientId) ?? null;
		const nextTerms = normalizeInvoicePaymentTerms(
			clientItem?.terms || clientItem?.Terms || form.clientSnapshot.terms || '',
		);
		const nextDueDate = computeDueDateForTerms(nextTerms, form.invoiceDate);
		const currentDueDate = normalizeIsoDate(form.dueDate);
		const computedDueDate = normalizeIsoDate(nextDueDate);
		return {
			current: currentDueDate || '',
			next: computedDueDate || '',
			terms: String(nextTerms ?? ''),
			willChange: Boolean(computedDueDate && computedDueDate !== currentDueDate),
		};
	})();

	const requiresConfirmation = editedLinesCount > 0 || Boolean(dueDateImpact?.willChange);

	return {
		requiresConfirmation,
		willRefreshCatalog,
		willRefreshRanges,
		rangesToRefreshCount: rangesToRefresh.length,
		editedLinesCount,
		editedRangesSummary,
		editedFieldsSummary,
		dueDateImpact,
	};
};

const fetchUpdatesConfirmTitle = computed(() => 'Fetch updates from journals?');

const fetchUpdatesConfirmMessage = computed(() => {
	const state = fetchUpdatesConfirmState.value;
	if (!state) return '';

	const parts = [];
	if (state.editedLinesCount > 0) {
		parts.push(
			`We detected edits to ${state.editedLinesCount} journal-derived line${state.editedLinesCount === 1 ? '' : 's'} in this invoice. Fetching updates will replace those lines with the latest journal data.`,
		);
	} else if (state.dueDateImpact?.willChange) {
		parts.push('Fetching updates will refresh client details and may adjust this invoice’s due date.');
	} else {
		parts.push('Fetching updates will refresh invoice data from the latest journals/catalog.');
	}

	parts.push('Custom items you added will be kept.');
	return parts.join(' ');
});

const fetchUpdatesConfirmDetails = computed(() => {
	const state = fetchUpdatesConfirmState.value;
	if (!state) return [];

	const details = [];
	if (state.willRefreshRanges) {
		const count = state.rangesToRefreshCount || 0;
		details.push(`Date ranges to refresh: ${count}`);
	}
	if (state.editedLinesCount > 0) {
		const fields = state.editedFieldsSummary || [];
		if (fields.length) {
			details.push(`Edits detected in: ${fields.join(', ')}`);
		}

		const ranges = state.editedRangesSummary || [];
		const maxRows = 5;
		ranges.slice(0, maxRows).forEach((entry) => {
			details.push(`${entry.label}: ${entry.count} edited line${entry.count === 1 ? '' : 's'}`);
		});
		if (ranges.length > maxRows) {
			details.push(`…and ${ranges.length - maxRows} more range${ranges.length - maxRows === 1 ? '' : 's'}.`);
		}
	}
	if (state.dueDateImpact?.willChange) {
		const next = state.dueDateImpact.next || '—';
		const current = state.dueDateImpact.current || '—';
		details.push(`Due date: ${current} → ${next} (based on client terms).`);
	}
	return details;
});

const enqueueInfoToast = (message) => {
	if (!message) return;
	if (isActiveTab.value) {
		toast?.info?.(message);
	} else {
		// Prevent spam when multiple events fire while tab is inactive
		if (pendingToastSet.has(message)) return;
		pendingToastSet.add(message);
		pendingToasts.value.push(message);
	}
};

const title = computed(() => {
	if (form.invoiceNumber) {
		return `${isCreditNoteMode.value ? 'Credit note' : 'Invoice'} #${form.invoiceNumber}`;
	}
	if (documentId.value) {
		return isCreditNoteMode.value ? 'Credit note' : 'Client invoice';
	}
	return isCreditNoteMode.value ? 'New credit note' : 'New client invoice';
});

const pageTitle = computed(() => {
	return buildEditorTitle();
});

const saveDisabled = computed(() => saving.value || snapshotLocked.value);

const showUnsavedChanges = computed(() => hasUnsavedChanges.value && ready.value);

watch(
	() => pageTitle.value,
	(title) => {
		if (title) {
			applyTabUpdates({ title });
		}
	},
	{ immediate: true },
);

const serializedFormSignature = computed(() => serializeFormSnapshot());

watch(
	() => serializedFormSignature.value,
	(current) => {
		if (!trackingReady.value) return;
		hasUnsavedChanges.value = current !== pristineSnapshot.value;
	},
);

const navigateBack = () => {
	if (listId.value) {
		clientInvoicesStore.setActiveList(listId.value);
	}
	emit('back-to-list', { listId: listId.value, tabId: myTabId.value || null });
};

const updateTabFromInvoice = (invoice) => {
	const creditMeta = invoice?.metadata?.creditNote || form.metadata?.creditNote || {};
	const sourceDocId =
		creditMeta.sourceInvoiceDocumentId || sourceCreditDocumentId.value || null;
	const sourceNumber =
		creditMeta.sourceInvoiceNumber || sourceCreditInvoiceNumber.value || null;
	const sourceQuotationDocumentId =
		invoice?.metadata?.sourceQuotation?.documentId ||
		form.metadata?.sourceQuotation?.documentId ||
		props.sourceQuotationDocumentId ||
		null;
	const updates = {
		props: {
			listId: listId.value,
			documentId: invoice?.documentId ?? null,
			draftId: invoice?.documentId ? null : draftId.value,
			mode: isCreditNoteMode.value ? 'credit-note' : 'invoice',
			sourceDocumentId: sourceDocId,
			sourceInvoiceNumber: sourceNumber,
			sourceQuotationDocumentId,
			initialInvoice: invoice?.documentId ? null : props.initialInvoice,
		},
	};
	if (invoice?.documentId) {
		updates.itemId = normalizeId(invoice.documentId);
	} else if (draftId.value) {
		updates.itemId = draftId.value;
	}
	updates.title = buildEditorTitle(invoice);
	applyTabUpdates(updates);
};

const ensureAmounts = () => {
	clearErrors();
	form.invoiceContent.dateRanges.forEach((range) => {
		if (range.dateFrom && !range.dateTo) {
			const fromDate = parseLocalDateValue(range.dateFrom);
			if (!fromDate) {
				range.dateTo = range.dateFrom;
				return;
			}
			const end = endOfWeek(fromDate, { weekStartsOn: 1 });
			range.dateTo = formatDateInputValue(end) || range.dateFrom;
		}
	});
	form.invoiceContent.dateRanges.forEach((range) => {
		(range.items || []).forEach((item) => {
			if (isCreditNoteMode.value) {
				normalizeLineForCreditNote(item);
			}
			const amount = calculateAmount(item);
			item.amount = Number(amount.toFixed(2));
		});
	});
	form.invoiceContent.customDates.forEach((entry) => {
		(entry.items || []).forEach((item) => {
			if (isCreditNoteMode.value) {
				normalizeLineForCreditNote(item);
			}
			const amount = calculateAmount(item);
			item.amount = Number(amount.toFixed(2));
		});
	});
	form.invoiceContent.currency = INVOICE_CURRENCY;
	const vatRateInput = normalizeVatRate(form.invoiceContent.vatRate);
	form.invoiceContent.vatRate = vatRateInput;
	const summary = calculateSummary(
		form.invoiceContent,
		vatRateInput,
	);
	form.invoiceContent.summary.subtotal = summary.subtotal;
	form.invoiceContent.summary.vat = summary.vat;
	form.invoiceContent.summary.total = summary.total;
	form.totalAmount = summary.total;
};

const clearAutoFetchedItemsForNoBuildings = () => {
	form.invoiceContent.dateRanges.forEach((range) => {
		const manualItems = (range.items || []).filter((item) => item.manual);
		range.items = manualItems;
		range.fullItems = [];
		range.missingServiceCount = 0;
		range.showFullItems = false;
		range.loadingFullItems = false;
	});
};

const refreshRangeItems = async () => {
	if (snapshotLocked.value) return;
	if (!activeBuildingIds.value.length) {
		clearAutoFetchedItemsForNoBuildings();
		return;
	}
	let lastError = null;
	for (const range of form.invoiceContent.dateRanges) {
		if (range.dateFrom && range.dateTo) {
			try {
				await fetchRangeItems(range);
			} catch (error) {
				lastError = error;
			}
		}
	}
	if (lastError) {
		throw lastError;
	}
};

const loadInvoice = async () => {
	// Always start from a clean slate to avoid leaking data between invoices
	resetForm();
	if (documentId.value) {
		hydratingContent.value = true;
		try {
			const invoice = await clientInvoicesStore.fetchInvoiceById(documentId.value);
			assignInvoiceToForm(invoice);
				// Prevent post-hydration watchers from auto-refreshing saved invoices.
				suppressAutoRefreshOnce.value = true;
				suppressSnapshotRefreshOnce.value = true;
				ensureAmounts();
			updateTabFromInvoice(invoice);
			markPristineSnapshot();
			ready.value = true;
			return;
		} catch (error) {
			logError(error);
			errorMessage.value = error?.message || 'Failed to load invoice.';
		} finally {
			hydratingContent.value = false;
		}
		ready.value = true;
		return;
	}
	if (isCreditNoteMode.value && sourceCreditDocumentId.value) {
		const loadedFromSource = await loadSourceInvoiceForCreditNote();
		if (loadedFromSource) {
			updateTabFromInvoice(null);
			markPristineSnapshot();
			ready.value = true;
			return;
		}
	}
	if (props.initialInvoice) {
		hydratingContent.value = true;
		try {
			assignInvoiceToForm(props.initialInvoice);
			ensureAmounts();
			updateTabFromInvoice(null);
			markPristineSnapshot();
			ready.value = true;
			return;
		} finally {
			hydratingContent.value = false;
		}
	}
	updateTabFromInvoice(null);
	ensureAmounts();
	markPristineSnapshot();
	ready.value = true;
};

const reloadInvoice = async () => {
	if (!documentId.value) return;
	await loadInvoice();
};

const getBusinessLogoUrl = async () => {
	let logoUrl = null;
	if (projectsStore.activeProject?.logo?.id) {
		try {
			const signed = await mediaStore.getSignedUrl(projectsStore.activeProject.logo.id);
			logoUrl = signed.url;
		} catch (error) {
			logError(error);
		}
	}
	// Fallback to stored URL if signing fails or no id (e.g., already public URL)
	if (!logoUrl && projectsStore.activeProject?.logo?.url) {
		logoUrl = projectsStore.activeProject.logo.url;
	}
	// Convert to data URL to avoid CORS issues in PDF generation
	if (logoUrl) {
		const dataUrl = await convertImageToDataUrl(logoUrl);
		return dataUrl || logoUrl; // Fallback to original URL if conversion fails
	}
	return null;
};

const previewInvoice = async () => {
	// Reset state before attempting preview to avoid inconsistent UI
	showPreview.value = false;
	previewHtml.value = '';
	errorMessage.value = '';
	try {
		ensureAmounts();
		const payload = buildPayload();
		const logoUrl = await getBusinessLogoUrl();
		const { html } = await generateInvoiceHtmlWithAutoSpacing({
			invoice: payload,
			projectInfo: {
				businessInfo: projectInfoData.value.businessInfo,
				businessName: businessName.value,
				bankDetails: projectBankDetails.value,
			},
			snapshot: cleanClientSnapshot(form.clientSnapshot),
			logoUrl: logoUrl || null,
		});
		previewHtml.value = html;
		showPreview.value = true;
	} catch (err) {
		// Ensure modal is closed on error
		showPreview.value = false;
		previewHtml.value = '';
		logError(err);
		errorMessage.value = err?.message || 'Unable to preview invoice.';
	}
};

const closePreview = () => {
	showPreview.value = false;
	previewHtml.value = '';
};

const waitForImages = async (root) => {
	if (!root) return;
	const imgs = Array.from(root.querySelectorAll('img'));
	if (!imgs.length) return;
	await Promise.all(
		imgs.map(
			(img) =>
				new Promise((resolve) => {
					if (img.complete && img.naturalWidth !== 0) {
						resolve();
						return;
					}
					const onComplete = () => {
						img.removeEventListener('load', onComplete);
						img.removeEventListener('error', onComplete);
						resolve();
					};
					img.addEventListener('load', onComplete);
					img.addEventListener('error', onComplete);
					setTimeout(onComplete, 1000);
				}),
		),
	);
};

const downloadPdfBytes = (pdfBytes, filename) => {
	const blob = new Blob([pdfBytes], { type: 'application/pdf' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	link.click();
	URL.revokeObjectURL(url);
};

const compressPdfBytes = async (pdfBytes) => {
	const { PDFDocument } = await import('pdf-lib');
	const input = pdfBytes instanceof ArrayBuffer ? new Uint8Array(pdfBytes) : pdfBytes;
	const pdfDoc = await PDFDocument.load(input, { ignoreEncryption: true });
	return pdfDoc.save({
		useObjectStreams: true,
		updateFieldAppearances: false,
		subsetFonts: true,
	});
};

const downloadPdf = async () => {
	try {
		pdfGenerating.value = true;
		errorMessage.value = '';
		ensureAmounts();
	const payload = buildPayload();
	const logoUrl = await getBusinessLogoUrl();
	const { html } = await generateInvoiceHtmlWithAutoSpacing({
		invoice: payload,
		projectInfo: {
			businessInfo: projectInfoData.value.businessInfo,
			businessName: businessName.value,
			bankDetails: projectBankDetails.value,
		},
		snapshot: cleanClientSnapshot(form.clientSnapshot),
		logoUrl: logoUrl || null,
	});
		const { default: html2pdf } = await import('html2pdf.js').catch(() => ({ default: null }));
		if (!html2pdf) {
			throw new Error('PDF generator not available. Please install html2pdf.js');
		}
		const exportScale = Math.max(2.5, window.devicePixelRatio || 1);
		const container = document.createElement('div');
		container.style.position = 'absolute';
		container.style.left = '-9999px';
		container.style.width = '8.27in';
		container.style.height = '11.68in';
		container.innerHTML = html;
		document.body.appendChild(container);
		try {
			const el =
				container.querySelector('.pdf-gen-invoice-document') ||
				container.querySelector('.pdf-gen-invoice-container') ||
				container;
			await waitForImages(el);
			const worker = html2pdf().from(el).set({
				margin: 0,
				filename: deriveInvoiceFilename(payload),
				image: { type: 'jpeg', quality: 0.85 },
				html2canvas: { scale: exportScale, useCORS: true, onclone: waitForClonedDocumentFonts },
				jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait', compress: true },
				pagebreak: { mode: ['css', 'legacy'] },
			});

			const pdfArrayBuffer = await worker.outputPdf('arraybuffer');
			if (!pdfArrayBuffer) {
				throw new Error('PDF generation failed');
			}

			let compressedPdfBytes = pdfArrayBuffer;
			try {
				compressedPdfBytes = await compressPdfBytes(pdfArrayBuffer);
			} catch (compressionError) {
				logError(compressionError);
			}

			downloadPdfBytes(compressedPdfBytes, deriveInvoiceFilename(payload));
		} finally {
			document.body.removeChild(container);
		}
	} catch (err) {
		logError(err);
		errorMessage.value = err?.message || 'Failed to generate PDF';
	} finally {
		pdfGenerating.value = false;
	}
};

const computeDueDateFromTerms = () => {
	if (isCreditNoteMode.value) {
		form.dueDate = '';
		return;
	}
	const termsRaw = form.clientSnapshot.terms;
	if (!termsRaw || !form.invoiceDate) return;
	const terms = normalizeInvoicePaymentTerms(termsRaw);
	form.clientSnapshot.terms = terms;
	if (isInvoiceDueOnReceipt(terms)) {
		form.dueDate = form.invoiceDate;
		return;
	}
	const num = Number(terms);
	if (!Number.isNaN(num)) {
		form.dueDate = addDays(form.invoiceDate, num);
	}
};

const onInvoiceDateChange = (value) => {
	form.invoiceDate = value;
	computeDueDateFromTerms();
};

const assignInvoiceToForm = (invoice) => {
	if (!invoice) return;
	const metadata = {
		snapshotFrozen: false,
		...(invoice.metadata || {}),
	};
	const loadedMode = normalizeEditorMode(metadata?.documentType);
	form.invoiceNumber = invoice.invoiceNumber || '';
	form.invoiceStatus = invoice.invoiceStatus || 'unpaid';
	form.invoiceDate = invoice.invoiceDate || '';
	form.dueDate = loadedMode === 'credit-note'
		? ''
		: (invoice.dueDate || addDays(invoice.invoiceDate || todayIso(), 14));
	form.purchaseOrder = normalizeOptionalText(invoice.purchaseOrder);
	form.paidDate = invoice.paidDate || '';
	form.totalAmount = invoice.totalAmount ?? '';
	form.notes = invoice.notes || '';
	form.clientId = invoice.client?.id || null;
	form.buildingId = invoice.building?.id || null;
	form.metadata = metadata;
	loadedDocumentMode.value = loadedMode;
	form.clientSnapshot = {
		...cleanClientSnapshot(invoice.clientSnapshot || {}),
	};
	if (
		loadedMode !== 'credit-note' &&
		isInvoiceDueOnReceipt(form.clientSnapshot.terms) &&
		form.invoiceDate
	) {
		form.dueDate = form.invoiceDate;
	}
	form.invoiceContent = {
		currency: INVOICE_CURRENCY,
		vatRate: normalizeVatRate(invoice.invoiceContent?.vatRate),
		buildingScope: form.buildingId ? 'single' : 'all',
		dateRanges: invoice.invoiceContent?.dateRanges
			? JSON.parse(JSON.stringify(invoice.invoiceContent.dateRanges))
			: [],
		customDates: invoice.invoiceContent?.customDates
			? JSON.parse(JSON.stringify(invoice.invoiceContent.customDates))
			: [],
		summary: {
			subtotal: invoice.invoiceContent?.summary?.subtotal ?? '',
			vat: invoice.invoiceContent?.summary?.vat ?? '',
			total: invoice.invoiceContent?.summary?.total ?? '',
		},
	};
	if (
		!form.buildingId &&
		!props.initialInvoice &&
		!invoice.metadata?.sourceQuotation?.documentId
	) {
		form.buildingId = null;
		form.clientSnapshot.buildingName = '';
	}
	// Ensure UI-only fields exist
	form.invoiceContent.dateRanges.forEach((range) => {
		range.sources = normalizeRangeSources(range);
		range.sourceType = deriveLegacySourceType(range.sources, range.sourceType || 'appointments');
		range.serviceGroupIds = range.sources.appointments.serviceGroupIds;
		range.serviceGroupId = range.serviceGroupIds[0] ?? null;
		range.cosId = range.sources.purchases.cosIds[0] ?? null;
		range.excludedPurchaseIds = normalizeIdList(range.excludedPurchaseIds);
		range.excludedAppointmentIds = normalizeIdList(range.excludedAppointmentIds);
		range.includedPurchaseIds = normalizeIdList(range.includedPurchaseIds);
		range.fullItems = range.fullItems || [];
		range.excludedPurchases = range.excludedPurchases || [];
		range.showFullItems = false;
		(range.items || []).forEach(ensureInvoiceLineSources);
		range.items = range.items || [];
	});
	form.invoiceContent.customDates.forEach((entry) => {
		(entry.items || []).forEach(ensureInvoiceLineSources);
		entry.items = entry.items || [];
	});
	snapshotLocked.value = invoice.invoiceStatus === 'paid' || invoice.metadata?.snapshotFrozen;
	if (snapshotLocked.value) {
		// Once locked, stop any further auto-refresh attempts
		snapshotLoading.value = false;
	}
};

const resetForm = () => {
	snapshotLocked.value = false;
	snapshotLoading.value = false;
	loadedDocumentMode.value = null;
	pendingCatalogUpdate.value = false;
	pendingSourceCatalogUpdate.value = false;
	pendingAppointmentsUpdate.value = false;
	pendingPurchasesUpdate.value = false;
	const today = todayIso();
	Object.assign(form, {
		invoiceNumber: initialInvoiceNumber.value || '',
		invoiceStatus: 'unpaid',
		invoiceDate: today,
		dueDate: propEditorMode.value === 'credit-note' ? '' : addDays(today, 14),
		purchaseOrder: '',
		paidDate: '',
		totalAmount: '',
		notes: '',
		clientId: null,
		buildingId: null,
		clientSnapshot: cleanClientSnapshot(),
		metadata: {
			snapshotFrozen: false,
			documentType: propEditorMode.value,
		},
		invoiceContent: {
			currency: INVOICE_CURRENCY,
			vatRate: INVOICE_VAT_RATE,
			buildingScope: 'all',
			dateRanges: [],
			customDates: [],
			summary: {
				subtotal: '',
				vat: '',
				total: '',
			},
		},
	});
};

const buildPayload = () => {
	if (!listId.value) throw new Error('Missing invoices list');
	if (form.invoiceStatus === 'paid' && !form.paidDate) {
		throw new Error('Paid invoices require a paid date.');
	}
	if (!form.clientId) throw new Error('Client is required.');
	if (!form.invoiceNumber) throw new Error('Invoice number is required.');
	const subtotalCheck = toNumberOrNull(form.invoiceContent.summary.subtotal);
	if (isCreditNoteMode.value && (subtotalCheck === null || subtotalCheck >= 0)) {
		throw new Error('Credit note subtotal must be below zero.');
	}
	if (!isCreditNoteMode.value && (subtotalCheck === null || subtotalCheck <= 0)) {
		throw new Error('Subtotal must be greater than zero.');
	}
	const totalAmount = toNumberOrNull(form.totalAmount);
	const summarySubtotal = toNumberOrNull(form.invoiceContent.summary.subtotal ?? totalAmount);
	const summaryVat = toNumberOrNull(form.invoiceContent.summary.vat);
	const summaryTotal =
		toNumberOrNull(form.invoiceContent.summary.total) ??
		(summarySubtotal !== null && summaryVat !== null ? summarySubtotal + summaryVat : totalAmount);

	const sanitizedSnapshot = cleanClientSnapshot(form.clientSnapshot);
	const existingMetadata = form.metadata || {};
	const existingCreditMeta =
		existingMetadata.creditNote && typeof existingMetadata.creditNote === 'object'
			? existingMetadata.creditNote
			: {};
	const sourceInvoiceDocumentId =
		existingCreditMeta.sourceInvoiceDocumentId || sourceCreditDocumentId.value || null;
	const sourceInvoiceNumber =
		existingCreditMeta.sourceInvoiceNumber || sourceCreditInvoiceNumber.value || null;
	if (isCreditNoteMode.value && !sourceInvoiceDocumentId) {
		throw new Error('Credit note source invoice is required.');
	}

	return {
		client_invoices_list: listId.value,
		invoiceNumber: form.invoiceNumber || null,
		invoiceStatus: form.invoiceStatus,
		invoiceDate: form.invoiceDate || null,
		dueDate: isCreditNoteMode.value ? null : (form.dueDate || null),
		purchaseOrder: normalizeOptionalText(form.purchaseOrder) || null,
		paidDate: form.paidDate || null,
		totalAmount,
		notes: form.notes || '',
		client: form.clientId || null,
		building: form.buildingId || null,
		clientSnapshot: sanitizedSnapshot,
		invoiceContent: {
			currency: INVOICE_CURRENCY,
			vatRate: normalizeVatRate(form.invoiceContent.vatRate),
			buildingScope: buildingScope.value,
			dateRanges: form.invoiceContent.dateRanges.map((range) => {
				const sources = normalizeRangeSources(range);
				const rangeServiceGroupIds = sources.appointments.serviceGroupIds;
				const rangeCosIds = sources.purchases.cosIds;
				return {
					dateFrom: range.dateFrom || '',
					dateTo: range.dateTo || '',
					sources,
					sourceType: deriveLegacySourceType(sources, range?.sourceType || 'appointments'),
					serviceGroupIds: rangeServiceGroupIds,
					serviceGroupId: rangeServiceGroupIds[0] ?? null,
					cosId: rangeCosIds[0] ?? null,
					excludedPurchaseIds: normalizeIdList(range?.excludedPurchaseIds),
					excludedAppointmentIds: normalizeIdList(range?.excludedAppointmentIds),
					includedPurchaseIds: normalizeIdList(range?.includedPurchaseIds),
					items: (range.items || []).map((item) => ({
						...item,
						hours: toNumberOrNull(item.hours),
						rate: toNumberOrNull(item.rate),
						directAmount: toNumberOrNull(item.directAmount),
						amount: toNumberOrNull(item.amount),
						amountMode:
							(item.amountMode || '').toLowerCase() === 'direct' ? 'direct' : 'calculated',
						manual: !!item.manual,
					})),
				};
			}),
			customDates: form.invoiceContent.customDates.map((entry) => {
				return {
					date: entry.date || '',
					items: (entry.items || []).map((item) => ({
						...item,
						hours: toNumberOrNull(item.hours),
						rate: toNumberOrNull(item.rate),
						directAmount: toNumberOrNull(item.directAmount),
						amount: toNumberOrNull(item.amount),
						amountMode:
							(item.amountMode || '').toLowerCase() === 'direct' ? 'direct' : 'calculated',
						manual: !!item.manual,
					})),
				};
			}),
			summary: {
				subtotal: summarySubtotal,
				vat: summaryVat,
				total: summaryTotal,
			},
		},
		metadata: {
			...existingMetadata,
			snapshotFrozen: form.invoiceStatus === 'paid',
			documentType: isCreditNoteMode.value
				? 'credit-note'
				: normalizeEditorMode(existingMetadata.documentType || 'invoice'),
			creditNote: isCreditNoteMode.value
				? {
						...existingCreditMeta,
						sourceInvoiceDocumentId,
						sourceInvoiceNumber,
				  }
				: undefined,
		},
	};
};

const handleSave = async () => {
	if (snapshotLocked.value) {
		toast?.info?.('This invoice is locked and cannot be edited.');
		return;
	}
	try {
		saving.value = true;
		errorMessage.value = '';
		const isNew = !documentId.value;
		if (!documentId.value && !form.invoiceNumber && listId.value) {
			if (isCreditNoteMode.value) {
				form.invoiceNumber = await ensureUniqueCreditNoteNumber(
					form.metadata?.creditNote?.sourceInvoiceNumber || sourceCreditInvoiceNumber.value || '',
					form.metadata?.creditNote?.sourceInvoiceDocumentId || sourceCreditDocumentId.value || '',
				);
			} else {
				const nextNum = await clientInvoicesStore.fetchNextInvoiceNumber(listId.value);
				if (nextNum) {
					form.invoiceNumber = nextNum;
				} else {
					throw new Error('Unable to generate invoice number');
				}
			}
		}
		if (
			isCreditNoteMode.value &&
			!documentId.value &&
			form.metadata?.creditNote?.sourceInvoiceNumber &&
			form.invoiceNumber
		) {
			form.invoiceNumber = await ensureUniqueCreditNoteNumber(
				form.metadata.creditNote.sourceInvoiceNumber,
				form.metadata?.creditNote?.sourceInvoiceDocumentId || sourceCreditDocumentId.value || '',
				documentId.value,
			);
		}
		if (!form.invoiceNumber) {
			throw new Error('Invoice number is required.');
		}
		if (isCreditNoteMode.value && !form.metadata?.creditNote?.sourceInvoiceDocumentId) {
			throw new Error('Credit note source invoice is required.');
		}
		ensureAmounts();
		const payload = buildPayload();
		const saved = await clientInvoicesStore.saveInvoice({
			documentId: documentId.value,
			payload,
		});
		hydratingContent.value = true;
		try {
			assignInvoiceToForm(saved);
		} finally {
			hydratingContent.value = false;
		}
		updateTabFromInvoice(saved);
		markPristineSnapshot({ enableTracking: true });
		const number = saved?.invoiceNumber || form.invoiceNumber || saved?.documentId || saved?.id || '';
		const documentLabel = isCreditNoteMode.value ? 'Credit note' : 'Invoice';
		const label = number ? `${documentLabel} ${number}` : documentLabel;
		const action = isNew ? 'created' : 'updated';
		toast?.success?.(`${label} ${action.toLowerCase()}.`);
	} catch (error) {
		logError(error);
		errorMessage.value = error?.message || 'Failed to save invoice';
	} finally {
		saving.value = false;
	}
};

const addRange = () => {
	form.invoiceContent.dateRanges.push({
		dateFrom: '',
		dateTo: '',
		items: [],
		sources: {
			appointments: { enabled: true, all: true, serviceGroupIds: [] },
			purchases: { enabled: false, all: false, cosIds: [] },
		},
		sourceType: 'appointments',
		serviceGroupIds: [],
		serviceGroupId: null,
		cosId: null,
		excludedPurchaseIds: [],
		excludedAppointmentIds: [],
		includedPurchaseIds: [],
		fullItems: [],
		excludedPurchases: [],
		showFullItems: false,
	});
};

const addCustomDate = () => {
	form.invoiceContent.customDates.push({
		date: '',
		items: [createManualInvoiceItem()],
	});
};

const removeDateRange = (entry) => {
	form.invoiceContent.dateRanges = form.invoiceContent.dateRanges.filter((r) => r !== entry);
};

const removeCustomDate = (entry) => {
	const idx = form.invoiceContent.customDates.indexOf(entry);
	if (idx !== -1) {
		form.invoiceContent.customDates.splice(idx, 1);
	}
};

const addRangeItem = (range) => {
	if (!range.items) range.items = [];
	range.items.push(createManualInvoiceItem());
};

const addCustomItemToCustomDate = (entry) => {
	if (!entry.items) entry.items = [];
	entry.items.push(createManualInvoiceItem());
};

const removeAllCustomItems = (entry) => {
	if (!entry) return;
	if (!Array.isArray(entry.items)) {
		entry.items = [];
		return;
	}
	// Preserve fetched grouped items; drop manual ones
	entry.items = entry.items.filter((item) => !item.manual);
};

const toggleFullItems = async (entry) => {
	if (!entry) return;
	const sources = normalizeRangeSources(entry);
	if (!hasAnySourcesSelected(sources)) {
		entry.showFullItems = false;
		return;
	}
	const wasShowing = entry.showFullItems;
	entry.showFullItems = !wasShowing;
	if (!wasShowing && (!entry.fullItems || !entry.fullItems.length)) {
		// Set loading state for this specific entry
		entry.loadingFullItems = true;
			try {
				await fetchRangeItems(entry, { updateMerged: false, updateFull: true });
			} catch (error) {
				logError(error);
				errorMessage.value = error?.message || 'Failed to load source items for this date range.';
				entry.showFullItems = false;
			} finally {
				entry.loadingFullItems = false;
		}
	}
};

const refreshSnapshotFromCatalog = async ({
	preserveExisting = Boolean(props.initialInvoice && !documentId.value),
} = {}) => {
	if (snapshotLocked.value) return;
	if (!form.clientId && !form.buildingId) return;
	snapshotLoading.value = true;
	try {
		const clientItem = await findCatalogItemById(form.clientId);
		const buildingItem = await findCatalogItemById(form.buildingId);
		if (clientItem) {
			form.clientSnapshot.name = preserveExisting
				? form.clientSnapshot.name || clientItem.name || ''
				: clientItem.name || form.clientSnapshot.name;
			form.clientSnapshot.address = preserveExisting
				? form.clientSnapshot.address || clientItem.address || ''
				: clientItem.address || form.clientSnapshot.address;
			form.clientSnapshot.customerId =
				(preserveExisting ? form.clientSnapshot.customerId : '') ||
				clientItem.customerId ||
				clientItem.customer_id ||
				clientItem['Customer ID'] ||
				form.clientSnapshot.customerId;
			form.clientSnapshot.terms = normalizeInvoicePaymentTerms(
				(preserveExisting ? form.clientSnapshot.terms : '') ||
				clientItem.terms ||
				clientItem.Terms ||
				form.clientSnapshot.terms,
			);
			form.clientSnapshot.email =
				(preserveExisting ? form.clientSnapshot.email : '') ||
				clientItem.email ||
				clientItem.Email ||
				form.clientSnapshot.email;
			form.clientSnapshot.telephone =
				(preserveExisting ? form.clientSnapshot.telephone : '') ||
				clientItem.telephone ||
				clientItem.phone ||
				clientItem.phoneNumber ||
				clientItem.Telephone ||
				clientItem.Phone ||
				form.clientSnapshot.telephone;
		}
		if (buildingScope.value === 'single') {
			if (buildingItem) {
				form.clientSnapshot.buildingName = preserveExisting
					? form.clientSnapshot.buildingName || buildingItem.name || ''
					: buildingItem.name || form.clientSnapshot.buildingName;
			}
		} else if (!preserveExisting) {
			form.clientSnapshot.buildingName = '';
		}
		computeDueDateFromTerms();
	} finally {
		snapshotLoading.value = false;
	}
};

const buildItemKey = ({ serviceId = null, flatRef = '', fallbackName = '', buildingId = null } = {}) => {
	const hasServiceId = serviceId !== null && serviceId !== undefined && serviceId !== '';
	const idPart = hasServiceId ? `id:${serviceId}` : `name:${fallbackName || ''}`;
	const buildingPart = buildingId ? `b:${buildingId}|` : '';
	return `${buildingPart}${idPart}#${flatRef || ''}`;
};

	const fetchAppointmentsForRange = async (range, { serviceGroupIds = [] } = {}) => {
		const buildingIds = activeBuildingIds.value;
		if (!buildingIds.length || !range.dateFrom || !range.dateTo) return [];
		const normalizedServiceGroupIds = normalizeIdList(serviceGroupIds);
		const fetchAppointments =
			typeof journalAppointmentsStore.queryAppointmentsInRange === 'function'
				? journalAppointmentsStore.queryAppointmentsInRange
				: journalAppointmentsStore.fetchAppointmentsInRange;
	return (
		(await fetchAppointments({
			startDate: range.dateFrom,
			endDate: range.dateTo,
				filters: {
					buildingId: buildingIds.length === 1 ? buildingIds[0] : null,
					buildingIds: buildingIds.length > 1 ? buildingIds : null,
					serviceGroupIds: normalizedServiceGroupIds.length ? normalizedServiceGroupIds : null,
				},
				pageSize: 100,
			})) || []
		);
	};

const buildServiceRateLookup = async () => {
	if (!catalogStore.initialized) {
		await catalogStore.bootstrap?.();
	}
	const serviceCatalog = catalogStore.catalogByName?.('Services');
	const serviceItems = (serviceCatalog && catalogStore.itemsByCatalogId?.[serviceCatalog.id]) || [];
	const serviceRates = new Map();
	serviceItems.forEach((service) => {
		const serviceId = service?.id;
		if (serviceId === null || serviceId === undefined || serviceId === '') return;
		const rate = toNumberOrNull(service?.rate);
		if (rate === null) return;
		serviceRates.set(String(serviceId), rate);
	});
	return (serviceId, explicitRate = null) => {
		const resolvedExplicitRate = toNumberOrNull(explicitRate);
		if (resolvedExplicitRate !== null) return resolvedExplicitRate;
		if (!serviceId) return null;
		return serviceRates.get(String(serviceId)) ?? null;
	};
};

const buildFullItemsFromAppointments = (appointments = [], findServiceRate) => {
	let missingServiceCount = 0;
	const fullItems = appointments.map((appt) => {
		const hasService = !!appt.service?.id;
		if (!hasService) {
			missingServiceCount += 1;
		}
		const serviceName = hasService ? appt.service?.name || 'Service' : 'Missing service';
		const flatRef = appt.flatRef || '';
		const rate = findServiceRate(appt.service?.id, appt.service?.rate);
		const supplierName = appt.supplier?.name || appt.supplier?.data?.attributes?.name || '';
		const buildingId = appt.building?.id ?? null;
		const buildingName = appt.building?.name || '';
		const sourceId = normalizeSourceId(appt.documentId ?? appt.id);
		const sourceDate = normalizeIsoDate(appt.date);
		return {
			description: serviceName,
			serviceId: appt.service?.id || null,
			serviceName,
			flatRef,
			hours: Number(appt.hours) || 0,
			rate: rate ?? '',
			amount: '',
			amountMode: 'calculated',
			directAmount: '',
			manual: false,
			supplierName,
			buildingId,
			buildingName,
			fullDisplay: true,
			missingService: !hasService,
			sourceType: 'appointments',
			sourceId,
			sourceAppointmentIds: [sourceId],
			sourceDate,
		};
	});
	return { fullItems, missingServiceCount };
};

const filterValidAppointments = (appointments = []) =>
	appointments.filter((appt) => appt.service?.id);

const isRefundedPurchase = (purchase = {}) =>
	String(purchase.documentStatus || '').toLowerCase().includes('refund');

const filterRefundedPurchases = (purchases = []) =>
	(purchases || []).filter((purchase) => !isRefundedPurchase(purchase));

const hasValidCosRelation = (purchase = {}) => {
	const cos = purchase?.cos;
	return Boolean(cos?.id);
};

const normalizeExcludedPurchaseIds = (range = {}) => normalizeIdList(range?.excludedPurchaseIds);

const normalizeExcludedAppointmentIds = (range = {}) => normalizeIdList(range?.excludedAppointmentIds);

const normalizeIncludedPurchaseIds = (range = {}) => normalizeIdList(range?.includedPurchaseIds);

const resolveCosMeta = (purchase = {}) => {
	const cos = purchase?.cos || {};
	return {
		cosId: cos?.id ?? null,
		cosName: cos?.name || 'COS',
	};
};

const fetchPurchasesForRange = async (range) => {
	const buildingIds = activeBuildingIds.value;
	if (!buildingIds.length || !range.dateFrom || !range.dateTo) return [];
	return (
		(await journalPurchasesStore.queryPurchasesInRange({
			startDate: range.dateFrom,
			endDate: range.dateTo,
			buildingId: buildingIds.length === 1 ? buildingIds[0] : null,
			buildingIds: buildingIds.length > 1 ? buildingIds : [],
			pageSize: 100,
		})) || []
	);
};

const resolveBuildingMeta = (entry) => {
	const entryBuildingId = entry?.building?.id ?? null;
	const entryBuildingName = entry?.building?.name || '';
	if (buildingScope.value === 'all') {
		return {
			buildingId: entryBuildingId,
			buildingName: entryBuildingName,
		};
	}
	return {
		buildingId: form.buildingId || entryBuildingId,
		buildingName: form.clientSnapshot.buildingName || entryBuildingName,
	};
};

const buildMergedAppointmentItems = (appointments = [], findServiceRate) => {
	const grouped = new Map();

	appointments.forEach((appt) => {
		const serviceId = appt.service?.id || null;
		if (!serviceId) return;
		const serviceName = appt.service?.name || 'Service';
		const flatRef = appt.flatRef || '';
		const hours = Number(appt.hours) || 0;
		const { buildingId, buildingName } = resolveBuildingMeta(appt);
		const key = buildItemKey({
			serviceId,
			flatRef,
			fallbackName: serviceName,
			buildingId: buildingScope.value === 'all' ? buildingId : null,
		});
		const rate = findServiceRate(serviceId, appt.service?.rate);

		if (!grouped.has(key)) {
			const item = {
				description: serviceName,
				serviceId,
				serviceName,
				flatRef,
				hours,
				rate: rate ?? '',
				amount: '',
				amountMode: 'calculated',
				directAmount: '',
				manual: false,
				buildingId,
				buildingName,
				sourceType: 'appointments',
				sourceAppointmentIds: [normalizeSourceId(appt.documentId ?? appt.id)],
			};
			ensureInvoiceLineSources(item);
			grouped.set(key, item);
			return;
		}

		const existing = grouped.get(key);
		existing.sourceAppointmentIds = [...new Set([...(existing.sourceAppointmentIds || []), normalizeSourceId(appt.documentId ?? appt.id)])];
		existing.hours = Number(toBig(existing.hours || 0).plus(hours).toFixed(2));
		existing.sourceHours = toNumberOrNull(existing.hours);
		if (toNumberOrNull(existing.rate) === null && rate !== null) {
			existing.rate = rate;
			existing.sourceRate = toNumberOrNull(rate);
		}
	});

	return Array.from(grouped.values());
};

const buildFullItemsFromPurchases = (purchases = [], { markup = true } = {}) => {
	return purchases.map((purchase) => {
		const vendorName = purchase.vendor?.name || '';
		const cosName = purchase.cos?.name || '';
		const gross = toNumberOrNull(purchase.gross) ?? 0;
		const grossWithMarkup = purchaseInvoiceAmount(gross, markup);
		const { buildingId, buildingName } = resolveBuildingMeta(purchase);
		const sourceId = normalizeSourceId(purchase.documentId ?? purchase.id);
		const sourceDate = normalizeIsoDate(purchase.date);
		const refunded = isRefundedPurchase(purchase);
		const hasCos = hasValidCosRelation(purchase);
		return {
			purchaseId: purchase.id ?? null,
			sourceType: 'purchases',
			sourceId,
			sourceDate,
			vendorId: purchase.vendor?.id ?? null,
			vendorName,
			cosId: purchase.cos?.id ?? null,
			cosName,
			description: vendorName,
			gross,
			grossWithMarkup,
			amount: grossWithMarkup,
			directAmount: grossWithMarkup,
			amountMode: 'direct',
			manual: false,
			buildingId,
			buildingName,
			refunded,
			missingCos: !hasCos,
		};
	});
};

const buildMergedPurchaseItems = (purchases = [], { includeMissingCos = false, markup = true } = {}) => {
	const grouped = new Map();

	purchases.forEach((purchase) => {
		const gross = purchaseInvoiceAmount(purchase.gross, markup);
		const { buildingId, buildingName } = resolveBuildingMeta(purchase);
		const hasCos = hasValidCosRelation(purchase);

		if (!hasCos) {
			if (!includeMissingCos) return;
			const vendorId = purchase.vendor?.id ?? null;
			const vendorName = purchase.vendor?.name || 'Missing COS';
			const key = buildItemKey({
				serviceId: vendorId ? `vendor:${vendorId}` : `vendor:${vendorName}`,
				fallbackName: vendorName,
				buildingId: buildingScope.value === 'all' ? buildingId : null,
			});
			const description = vendorName ? `${vendorName} (Missing COS)` : 'Missing COS';
			if (!grouped.has(key)) {
				const item = {
					description,
					flatRef: '',
					hours: '',
					rate: '',
					amountMode: 'direct',
					directAmount: Number(toBig(gross).toFixed(2)),
					amount: '',
					manual: false,
					buildingId,
					buildingName,
					vendorId,
					vendorName,
					missingCos: true,
					sourceType: 'purchases',
				};
				ensureInvoiceLineSources(item);
				grouped.set(key, item);
				return;
			}
			const existing = grouped.get(key);
			const next = toBig(existing.directAmount || 0).plus(gross);
			existing.directAmount = Number(next.toFixed(2));
			existing.sourceDirectAmount = toNumberOrNull(existing.directAmount);
			return;
		}

		const { cosId, cosName } = resolveCosMeta(purchase);
		if (!cosId) return;
		const key = buildItemKey({
			serviceId: cosId,
			fallbackName: cosName,
			buildingId: buildingScope.value === 'all' ? buildingId : null,
		});

		if (!grouped.has(key)) {
			const item = {
				description: cosName,
				flatRef: '',
				hours: '',
				rate: '',
				amountMode: 'direct',
				directAmount: Number(toBig(gross).toFixed(2)),
				amount: '',
				manual: false,
				buildingId,
				buildingName,
				sourceType: 'purchases',
			};
			ensureInvoiceLineSources(item);
			grouped.set(key, item);
			return;
		}

		const existing = grouped.get(key);
		const next = toBig(existing.directAmount || 0).plus(gross);
		existing.directAmount = Number(next.toFixed(2));
		existing.sourceDirectAmount = toNumberOrNull(existing.directAmount);
	});

	return Array.from(grouped.values());
};

const hasAnySourcesSelected = (sources = {}) =>
	Boolean(sources?.appointments?.enabled || sources?.purchases?.enabled);

const buildAppointmentSourceData = async (range, sources) => {
	if (!sources.appointments.enabled) {
		return { fullItems: [], mergedItems: [], missingServiceCount: 0 };
	}
	const serviceGroupIds = sources.appointments.all ? [] : sources.appointments.serviceGroupIds;
	const appointments = await fetchAppointmentsForRange(range, { serviceGroupIds });
	const findServiceRate = await buildServiceRateLookup();
	const { fullItems, missingServiceCount } = buildFullItemsFromAppointments(appointments, findServiceRate);
	const excludedIds = new Set(normalizeExcludedAppointmentIds(range).map(String));
	const includedAppointments = appointments.filter((appt) => {
		const sourceId = normalizeSourceId(appt.documentId ?? appt.id);
		if (!sourceId) return false;
		if (excludedIds.has(sourceId)) return false;
		return !!appt.service?.id;
	});
	const mergedItems = buildMergedAppointmentItems(includedAppointments, findServiceRate);
	const normalizedFullItems = fullItems.map((item) => {
		const excludedByManual = item.sourceId && excludedIds.has(String(item.sourceId));
		const excludedByMissingService = item.missingService;
		const excluded = excludedByManual || excludedByMissingService;
		const excludedReason = excludedByManual ? 'manual' : item.missingService ? 'missing-service' : '';
		return {
			...item,
			excluded,
			excludedReason,
		};
	});
	return { fullItems: normalizedFullItems, mergedItems, missingServiceCount };
};

const buildPurchaseSourceData = async (range, sources) => {
	if (!sources.purchases.enabled) {
		return { fullItems: [], mergedItems: [] };
	}
	const purchases = await fetchPurchasesForRange(range);
	const cosIds = sources.purchases.all ? [] : sources.purchases.cosIds;
	const cosIdSet = new Set((cosIds || []).map((id) => String(id)));
	const filteredPurchases = cosIds.length
		? purchases.filter((purchase) => cosIdSet.has(String(purchase?.cos?.id ?? '')))
		: purchases;
	const excludedIds = new Set(normalizeExcludedPurchaseIds(range).map(String));
	const includedIds = new Set(normalizeIncludedPurchaseIds(range).map(String));
	const includeMissingCos = Boolean(sources.purchases.all);
	const markup = sources.purchases.markup !== false;
	const mergedPurchases = filteredPurchases.filter((purchase) => {
		const sourceId = normalizeSourceId(purchase.documentId ?? purchase.id);
		if (sourceId && excludedIds.has(sourceId)) return false;
		const refunded = isRefundedPurchase(purchase);
		if (refunded && !(sourceId && includedIds.has(sourceId))) return false;
		if (!hasValidCosRelation(purchase) && !includeMissingCos) return false;
		return true;
	});
	const mergedItems = buildMergedPurchaseItems(mergedPurchases, { includeMissingCos, markup });
	const fullItems = buildFullItemsFromPurchases(filteredPurchases, { markup }).map((item) => {
		const sourceId = item.sourceId ? String(item.sourceId) : null;
		const manualExcluded = sourceId && excludedIds.has(sourceId);
		const refundExcluded = item.refunded && !(sourceId && includedIds.has(sourceId));
		const excluded = manualExcluded || refundExcluded;
		const excludedReason = manualExcluded ? 'manual' : refundExcluded ? 'refund' : '';
		return {
			...item,
			excluded,
			excludedReason,
		};
	});
	return { fullItems, mergedItems };
};

const nextRangeFetchToken = (range) => {
	range._fetchToken = (range._fetchToken || 0) + 1;
	return range._fetchToken;
};

const isRangeFetchCurrent = (range, token) => range._fetchToken === token;

const fetchRangeItems = async (range, { updateMerged = true, updateFull = true } = {}) => {
	if (!activeBuildingIds.value.length || !range.dateFrom || !range.dateTo) return;
	const sources = normalizeRangeSources(range);
	range.sources = sources;
	if (!hasAnySourcesSelected(sources)) {
		if (updateMerged) {
			const manualItems = (range.items || []).filter((item) => item.manual);
			range.items = [...manualItems];
		}
		if (updateFull) {
			range.fullItems = [];
		}
		range.missingServiceCount = 0;
		return;
	}
	const token = nextRangeFetchToken(range);
	isFetchingRanges.value = true;
	try {
		const [appointmentData, purchaseData] = await Promise.all([
			buildAppointmentSourceData(range, sources),
			buildPurchaseSourceData(range, sources),
		]);
		if (!isRangeFetchCurrent(range, token)) return;
		if (updateMerged) {
			const manualItems = (range.items || []).filter((item) => item.manual);
			const mergedItems = [
				...appointmentData.mergedItems,
				...purchaseData.mergedItems,
				...manualItems,
			];
			range.items = mergedItems;
		}
		if (updateFull) {
			range.fullItems = [...appointmentData.fullItems, ...purchaseData.fullItems];
		}
		range.missingServiceCount = appointmentData.missingServiceCount || 0;
	} finally {
		if (isRangeFetchCurrent(range, token)) {
			isFetchingRanges.value = false;
		}
	}
};

const refreshRange = async (range) => {
	if (!range) return;
	try {
		await fetchRangeItems(range);
	} catch (error) {
		logError(error);
		errorMessage.value = error?.message || 'Failed to load invoice items.';
	} finally {
		ensureAmounts();
	}
};

watch(
	() => documentId.value,
	async (next, prev) => {
		if (!bootstrapped.value || next === prev) {
			return;
		}
		if (next) {
			await loadInvoice();
		} else {
			resetForm();
			updateTabFromInvoice(null);
			markPristineSnapshot();
		}
	},
);

watch(
	() => draftId.value,
	async (next, prev) => {
		if (!bootstrapped.value || next === prev) {
			return;
		}
		await nextTick();
		if (documentId.value) {
			return;
		}
		resetForm();
		updateTabFromInvoice(null);
		markPristineSnapshot();
	},
);

watch(
	() => [propEditorMode.value, sourceCreditDocumentId.value],
	async (curr, prev = []) => {
		if (!bootstrapped.value || documentId.value) return;
		const [nextMode, nextSource] = curr;
		const [prevMode, prevSource] = prev;
		if (nextMode === prevMode && nextSource === prevSource) return;
		await loadInvoice();
	},
	{ deep: false },
);

watch(
	() => listId.value,
	(newId) => {
		if (newId) {
			clientInvoicesStore.setActiveList(newId);
		} else if (clientInvoicesStore.lists.length) {
			clientInvoicesStore.setActiveList(clientInvoicesStore.lists[0].id);
		}
	},
	{ immediate: true },
);

watch(
	() => [form.clientId, form.buildingId],
	async (curr, prev = []) => {
		if (snapshotLocked.value || hydratingContent.value) return;
		if (suppressSnapshotRefreshOnce.value) {
			suppressSnapshotRefreshOnce.value = false;
			return;
		}
		const [nextClientId, nextBuildingId] = curr;
		const [prevClientId, prevBuildingId] = prev;
		if (nextClientId === prevClientId && nextBuildingId === prevBuildingId) return;
		try {
			await refreshSnapshotFromCatalog();
		} catch (error) {
			logError(error);
		} finally {
			ensureAmounts();
		}
	},
);

watch(
	() => form.clientSnapshot.terms,
	() => {
		if (!snapshotLocked.value) {
			computeDueDateFromTerms();
		}
	},
);

watch(
		() => ({
			buildingKey: activeBuildingIds.value.join('|'),
			buildingScope: buildingScope.value,
			rangeKeys: form.invoiceContent.dateRanges.map((r) => {
				const sources = normalizeRangeSources(r);
				const appointmentKey = sources.appointments.enabled
					? sources.appointments.all
						? 'appointments:all'
						: `appointments:${sources.appointments.serviceGroupIds
								.map((id) => String(id))
								.sort()
								.join(',')}`
					: 'appointments:none';
				const purchaseKey = sources.purchases.enabled
					? `${sources.purchases.all
						? 'purchases:all'
						: `purchases:${sources.purchases.cosIds.map((id) => String(id)).sort().join(',')}`}${sources.purchases.markup ? '' : ':no-markup'}`
					: 'purchases:none';
				return `${r.dateFrom || ''}|${r.dateTo || ''}|${appointmentKey}|${purchaseKey}`;
			}),
		}),
	async (curr, prev) => {
		if (snapshotLocked.value || hydratingContent.value) return;
		if (suppressAutoRefreshOnce.value) {
			suppressAutoRefreshOnce.value = false;
			return;
		}
		// Skip initial auto-refresh for saved invoices on load.
		if (documentId.value && !bootstrapped.value) return;
		if (
			prev &&
			curr.buildingKey === prev.buildingKey &&
			curr.buildingScope === prev.buildingScope &&
			JSON.stringify(curr.rangeKeys) === JSON.stringify(prev.rangeKeys)
		) {
			return;
		}
			try {
				await refreshRangeItems();
			} catch (error) {
				logError(error);
				errorMessage.value = error?.message || 'Failed to load invoice items.';
			} finally {
				ensureAmounts();
			}
	},
	{ deep: false },
);

watch(
	() => form.invoiceContent,
	() => {
		ensureAmounts();
	},
	{ deep: true },
);

watch(
	() => form.invoiceStatus,
	(newStatus) => {
		if (newStatus === 'paid' && !form.paidDate) {
			form.paidDate = form.invoiceDate || todayIso();
		}
		if (newStatus !== 'paid') {
			form.paidDate = '';
		}
	},
);

watch(
	() => form.invoiceDate,
	() => {
		computeDueDateFromTerms();
	},
);

// Auto-extend range end whenever "From" changes (defaults to week end, Mon-Sun)
watch(
	() => form.invoiceContent.dateRanges.map((r) => r.dateFrom || ''),
	(newFroms, oldFroms = []) => {
		// Avoid clobbering saved values when hydrating an existing invoice
		if (hydratingContent.value && !oldFroms.length) return;

		form.invoiceContent.dateRanges.forEach((range, idx) => {
			const nextFrom = newFroms[idx] || '';
			const prevFrom = oldFroms[idx] || '';
			if (nextFrom === prevFrom) return;

			if (!nextFrom) {
				range.dateTo = '';
				return;
			}

			// Skip auto-adjustment while hydrating saved data to preserve stored ranges
			if (hydratingContent.value) return;

			// If the previous end date was manually set, don't override it automatically
			const prevDate = parseLocalDateValue(prevFrom);
			const prevWeekEnd = prevDate ? formatDateInputValue(endOfWeek(prevDate, { weekStartsOn: 1 })) : null;
			const prevWasAuto = !range.dateTo || range.dateTo === prevWeekEnd;
			if (!prevWasAuto && !hydratingContent.value) return;

			const nextDate = parseLocalDateValue(nextFrom);
			if (!nextDate) {
				range.dateTo = nextFrom;
				return;
			}
			const end = endOfWeek(nextDate, { weekStartsOn: 1 });
			range.dateTo = formatDateInputValue(end) || nextFrom;
		});
	},
	{ deep: false },
);

const refreshSnapshotWithToast = async () => {
	await refreshSnapshotFromCatalog();
	enqueueInfoToast('Client/building details updated after recent catalog updates.');
};

const rangesImpactedByAppointmentChanges = (dates = [], buildingIds = []) => {
	const normalizedDates = (dates || []).map(normalizeIsoDate).filter(Boolean);
	const normalizedBuildingIds = (buildingIds || []).filter(Boolean).map(String);
	const activeIds = activeBuildingIds.value.map(String);
	if (!activeIds.length) return [];
	if (!normalizedBuildingIds.length) return [];
	const intersects = normalizedBuildingIds.some((id) => activeIds.includes(id));
	if (!intersects) return [];
	return form.invoiceContent.dateRanges.filter((range) => {
		if (!hasAppointmentsSource(range)) return false;
		if (!range.dateFrom || !range.dateTo) return false;
		if (!normalizedDates.length) return false;
		return normalizedDates.some((date) => date >= range.dateFrom && date <= range.dateTo);
	});
};

const rangesImpactedByPurchaseChanges = (dates = [], buildingIds = []) => {
	const normalizedDates = (dates || []).map(normalizeIsoDate).filter(Boolean);
	const normalizedBuildingIds = (buildingIds || []).filter(Boolean).map(String);
	const activeIds = activeBuildingIds.value.map(String);
	if (!activeIds.length) return [];
	if (!normalizedBuildingIds.length) return [];
	const intersects = normalizedBuildingIds.some((id) => activeIds.includes(id));
	if (!intersects) return [];
	return form.invoiceContent.dateRanges.filter((range) => {
		if (!hasPurchasesSource(range)) return false;
		if (!range.dateFrom || !range.dateTo) return false;
		if (!normalizedDates.length) return false;
		return normalizedDates.some((date) => date >= range.dateFrom && date <= range.dateTo);
	});
};

const refreshImpactedRanges = async (dates = [], buildingIds = []) => {
	const normalizedDates = (dates || []).map(normalizeIsoDate).filter(Boolean);
	const normalizedBuildingIds = (buildingIds || []).filter(Boolean).map(String);
	const activeIds = activeBuildingIds.value.map(String);
	if (!activeIds.length) return;
	if (!normalizedBuildingIds.length) return;
	const intersects = normalizedBuildingIds.some((id) => activeIds.includes(id));
	if (!intersects) return;
	const ranges = form.invoiceContent.dateRanges.filter((range) => {
		if (!hasAppointmentsSource(range)) return false;
		if (!range.dateFrom || !range.dateTo) return false;
		if (!normalizedDates.length) return true;
		return normalizedDates.some((date) => date >= range.dateFrom && date <= range.dateTo);
	});
	if (!ranges.length) return;
	let lastError = null;
	for (const range of ranges) {
		try {
			await fetchRangeItems(range);
		} catch (error) {
			lastError = error;
		}
	}
	ensureAmounts();
	if (lastError) {
		throw lastError;
	}
	enqueueInfoToast('Appointments changed. Items were refreshed for the affected dates.');
};

const refreshImpactedPurchaseRanges = async (dates = [], buildingIds = []) => {
	const normalizedDates = (dates || []).map(normalizeIsoDate).filter(Boolean);
	const normalizedBuildingIds = (buildingIds || []).filter(Boolean).map(String);
	const activeIds = activeBuildingIds.value.map(String);
	if (!activeIds.length) return;
	if (!normalizedBuildingIds.length) return;
	const intersects = normalizedBuildingIds.some((id) => activeIds.includes(id));
	if (!intersects) return;
	const ranges = form.invoiceContent.dateRanges.filter((range) => {
		if (!hasPurchasesSource(range)) return false;
		if (!range.dateFrom || !range.dateTo) return false;
		if (!normalizedDates.length) return true;
		return normalizedDates.some((date) => date >= range.dateFrom && date <= range.dateTo);
	});
	if (!ranges.length) return;
	let lastError = null;
	for (const range of ranges) {
		try {
			await fetchRangeItems(range);
		} catch (error) {
			lastError = error;
		}
	}
	ensureAmounts();
	if (lastError) {
		throw lastError;
	}
	enqueueInfoToast('Purchases changed. Items were refreshed for the affected dates.');
};

const catalogChangedUnsub = onEvent('catalog:changed', async (payload = {}) => {
	if (snapshotLocked.value) return;
	const ids = payload?.ids || [];
	const all = payload?.all;
	const clientMatch = form.clientId && ids.some((id) => String(id) === String(form.clientId));
	const buildingMatch = form.buildingId && ids.some((id) => String(id) === String(form.buildingId));
	const serviceCatalogMatch = isServiceCatalogEvent(payload);
	const serviceGroupMatch = isServiceGroupEvent(payload);
	const shouldRefreshSnapshot = Boolean(all || clientMatch || buildingMatch);
	const shouldRefreshSources = Boolean(all || serviceCatalogMatch || serviceGroupMatch);

	if (!shouldRefreshSnapshot && !shouldRefreshSources) return;
	if (!autoSyncEnabled.value) {
		if (shouldRefreshSnapshot) {
			pendingCatalogUpdate.value = true;
		}
		if (shouldRefreshSources) {
			pendingSourceCatalogUpdate.value = true;
		}
		if (shouldRefreshSnapshot && shouldRefreshSources) {
			enqueueInfoToast(
				'Catalog data changed. Saved invoices do not auto-update — click "Fetch updates" to refresh.',
			);
		} else if (shouldRefreshSnapshot) {
			enqueueInfoToast(
				'Client/building catalog data changed. Saved invoices do not auto-update — click "Fetch updates" to refresh.',
			);
		} else {
			enqueueInfoToast(
				'Service catalog data changed. Saved invoices do not auto-update — click "Fetch updates" to refresh items.',
			);
		}
		return;
	}

		if (shouldRefreshSnapshot) {
			try {
				await refreshSnapshotWithToast();
			} catch (error) {
				logError(error);
				errorMessage.value = error?.message || 'Failed to refresh client/building details.';
			}
		}

	if (shouldRefreshSources) {
			try {
				await catalogStore.ensureServiceGroups?.({ force: true });
				await refreshRangeItems();
				ensureAmounts();
				enqueueInfoToast('Invoice items refreshed after service catalog updates.');
			} catch (error) {
				logError(error);
				errorMessage.value = error?.message || 'Failed to refresh invoice items.';
			}
		}
});

const appointmentsChangedUnsub = onEvent('appointments:changed', async (payload = {}) => {
	if (snapshotLocked.value) return;
	const dates = payload?.dates || [];
	const appointmentIds = payload?.appointmentIds || [];
	const buildingIds =
		Array.isArray(payload?.buildingIds) && payload.buildingIds.length
			? payload.buildingIds
			: payload?.buildingId
				? [payload.buildingId]
				: [];
	if (!dates.length && !appointmentIds.length) {
		return;
	}
	if (!autoSyncEnabled.value) {
		const impacted = rangesImpactedByAppointmentChanges(dates, buildingIds);
		if (!impacted.length) {
			return;
		}
		if (!pendingAppointmentsUpdate.value) {
			pendingAppointmentsUpdate.value = true;
			enqueueInfoToast(
				'Appointments changed since this invoice was saved. Click "Fetch updates" if you want to refresh items.',
			);
		} else {
			pendingAppointmentsUpdate.value = true;
		}
		return;
	}
		try {
			await refreshImpactedRanges(dates, buildingIds);
		} catch (error) {
			logError(error);
			errorMessage.value = error?.message || 'Failed to refresh invoice items.';
		}
	});

const purchasesChangedUnsub = onEvent('purchases:changed', async (payload = {}) => {
	if (snapshotLocked.value) return;
	const dates = payload?.dates || [];
	const purchaseIds = payload?.purchaseIds || [];
	const buildingIds =
		Array.isArray(payload?.buildingIds) && payload.buildingIds.length
			? payload.buildingIds
			: payload?.buildingId
				? [payload.buildingId]
				: [];
	if (!dates.length && !purchaseIds.length) {
		return;
	}
	if (!autoSyncEnabled.value) {
		const impacted = rangesImpactedByPurchaseChanges(dates, buildingIds);
		if (!impacted.length) {
			return;
		}
		if (!pendingPurchasesUpdate.value) {
			pendingPurchasesUpdate.value = true;
			enqueueInfoToast(
				'Purchases changed since this invoice was saved. Click "Fetch updates" if you want to refresh items.',
			);
		} else {
			pendingPurchasesUpdate.value = true;
		}
		return;
	}
		try {
			await refreshImpactedPurchaseRanges(dates, buildingIds);
		} catch (error) {
			logError(error);
			errorMessage.value = error?.message || 'Failed to refresh invoice items.';
		}
	});

const handleFetchUpdates = async () => {
	if (snapshotLocked.value) return;
	try {
		errorMessage.value = '';
		if (pendingCatalogUpdate.value) {
			await refreshSnapshotFromCatalog();
		}
		if (pendingSourceCatalogUpdate.value) {
			await catalogStore.ensureServiceGroups?.({ force: true });
		}
		if (
			pendingAppointmentsUpdate.value ||
			pendingPurchasesUpdate.value ||
			pendingSourceCatalogUpdate.value
		) {
			await refreshRangeItems();
		}
		ensureAmounts();
		pendingCatalogUpdate.value = false;
		pendingSourceCatalogUpdate.value = false;
		pendingAppointmentsUpdate.value = false;
		pendingPurchasesUpdate.value = false;
		toast?.success?.('Invoice data refreshed.');
	} catch (err) {
		logError(err);
		errorMessage.value = err?.message || 'Failed to refresh invoice data';
	}
};

const handleFetchUpdatesClick = async () => {
	if (snapshotLocked.value) return;
	if (saving.value || isFetchingRanges.value) return;
	const impact = analyzeFetchUpdatesImpact();
	if (!impact.requiresConfirmation) {
		await handleFetchUpdates();
		return;
	}
	fetchUpdatesConfirmState.value = impact;
	showFetchUpdatesConfirm.value = true;
};

const cancelFetchUpdatesConfirm = () => {
	showFetchUpdatesConfirm.value = false;
	fetchUpdatesConfirmState.value = null;
};

const confirmFetchUpdatesConfirm = async () => {
	showFetchUpdatesConfirm.value = false;
	await handleFetchUpdates();
	fetchUpdatesConfirmState.value = null;
};

onMounted(async () => {
	try {
		await projectsStore.ensureInitialized?.();
	} catch (error) {
		logError(error);
	}
	try {
		await clientInvoicesStore.initialize();
	} catch (error) {
		logError(error);
	}
	try {
		await catalogStore.ensureServiceGroups?.();
	} catch (error) {
		logError(error);
	}
	if (!listId.value && clientInvoicesStore.lists.length) {
		clientInvoicesStore.setActiveList(clientInvoicesStore.lists[0].id);
	}
	try {
		await nextTick();
		await ensureInvoiceNumberSeeded();
		await loadInvoice();
	} catch (error) {
		logError(error);
		errorMessage.value = error?.message || 'Failed to load invoice.';
	}
	// Only auto-sync for brand new invoices (no documentId yet).
	if (autoSyncEnabled.value) {
		try {
			await refreshSnapshotFromCatalog();
			await ensureInvoiceNumberSeeded();
			await refreshRangeItems();
		} catch (error) {
			logError(error);
			errorMessage.value = error?.message || 'Failed to load invoice source data.';
		}
	}
	markPristineSnapshot({ enableTracking: true });
	bootstrapped.value = true;
});

onUnmounted(() => {
	catalogChangedUnsub?.();
	appointmentsChangedUnsub?.();
	purchasesChangedUnsub?.();
});

watch(
	() => isActiveTab.value,
	(active) => {
		if (active && pendingToasts.value.length) {
			pendingToasts.value.forEach((msg) => toast?.info?.(msg));
			pendingToasts.value = [];
			pendingToastSet.clear();
		}
	},
	{ immediate: true },
);

watch(
	() => listId.value,
	() => {
		ensureInvoiceNumberSeeded();
	},
);
</script>

<style scoped>
.invoice-creator :deep(.creator-title) {
	font-family: var(--font-family-display, "SF Pro Display", sans-serif);
}

.invoice-creator {
	padding-bottom: 200px;
}

.invoice-editor--autumn {
	/* Decorative background stays behind the form and cannot intercept pointer events. */
	background-color: var(--noc-background);
	background-image:
		linear-gradient(180deg, transparent 180px, var(--noc-background) 340px),
		url('@/assets/images/autumn-invoice-dragon-corner.webp');
	background-position: right top;
	background-size: 100% 360px, clamp(360px, 38vw, 560px) auto;
	background-repeat: no-repeat;
}

@media (max-width: 980px) {
	.invoice-editor--autumn {
		background-image:
			linear-gradient(180deg, transparent, var(--noc-background) 160px),
			url('@/assets/images/autumn-invoice-dragon-corner.webp');
		background-size: 100% 200px, 280px auto;
	}
}

@media (max-width: 650px) {
	.invoice-editor--autumn {
		background-image:
			linear-gradient(180deg, transparent, var(--noc-background-secondary, #fafafb) 110px),
			url('@/assets/images/autumn-invoice-dragon-corner.webp');
		background-size: 100% 160px, 220px auto;
	}
}

@media print {
	.invoice-editor--autumn {
		background: none;
	}
}

.panel {
	background: var(--noc-background);
	border: 1px solid var(--border-color);
	border-radius: 12px;
	padding: 18px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	box-shadow: var(--box-shadow);
}

.panel.no-border-panel {
	background: transparent;
	border: none;
	box-shadow: none;
	padding: 0;
	border-radius: 0;
}

.panel h3 {
	margin: 0;
	font-size: 1rem;
}

.credit-note-banner {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	align-items: center;
	margin-top: 12px;
	padding: 10px 12px;
	border-radius: 4px;
	border: none;
	background: var(--noc-blue-05);
	color: var(--noc-blue);
	font-size: 0.9rem;
	font-weight: 500;
}

.credit-note-banner-title {
	font-weight: 700;
}

.credit-note-banner-text {
	font-weight: 500;
}

.items-manager {
	margin-bottom: 12px;
}

.form-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
	gap: 12px;
}

label {
	display: flex;
	flex-direction: column;
	gap: 6px;
	font-size: 0.85rem;
	color: var(--text-color-secondary);
}

label span {
	font-weight: 600;
	color: var(--text-color);
}

input,
select,
textarea {
	border: 1px solid var(--border-color);
	border-radius: 8px;
	padding: 10px 12px;
	font-size: 0.95rem;
	background: var(--noc-background);
	color: var(--text-color);
}

textarea {
	min-height: 80px;
}

.loading-state {
	padding: 24px;
	text-align: center;
	color: var(--text-color-secondary);
}

:deep(.invoice-creator) {
	padding-bottom: 180px;
}

:deep(.creator-footer) {
	position: fixed;
	z-index: 90;
	left: calc(220px + 24px);
	bottom: 22px;
	transform: none;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 20px;
	width: max-content;
	max-width: calc(100vw - 268px);
	margin: 0;
	padding: 12px 18px;
	border: 1px solid rgba(255, 255, 255, 0.58);
	border-radius: 12px;
	background:
		linear-gradient(180deg, rgba(255, 255, 255, 0.74), rgba(255, 255, 255, 0.54));
	box-shadow:
		0 18px 42px rgba(15, 23, 42, 0.13),
		0 2px 8px rgba(15, 23, 42, 0.06),
		inset 0 1px 0 rgba(255, 255, 255, 0.72);
	backdrop-filter: blur(22px) saturate(1.35);
	-webkit-backdrop-filter: blur(22px) saturate(1.35);
}

:deep(.creator-footer button) {
	min-height: 32px;
}

.floating-action-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 7px;
	border: none;
	background: transparent;
	color: var(--noc-blue, #0065ff);
	cursor: pointer;
	font-weight: 600;
	padding: 0;
	border-radius: 0;
	height: 32px;
	min-height: 32px;
	box-shadow: none;
	transition: color 0.1s ease, opacity 0.1s ease;
	font-size: 0.86rem;
	white-space: nowrap;
}

.floating-action-btn:hover:not(:disabled) {
	background: transparent;
	border-color: transparent;
	color: var(--noc-blue, #0065ff);
	box-shadow: none;
	opacity: 0.82;
}

.floating-action-btn:focus-visible {
	outline: none;
	background: var(--noc-background-secondary, #fafafa);
}

.floating-action-btn:active:not(:disabled) {
	transform: none;
}

.floating-action-btn:disabled {
	cursor: not-allowed;
	opacity: 0.55;
}

.floating-action-btn--primary {
	color: var(--noc-blue, #0065ff);
	font-weight: 600;
}

.floating-action-btn--primary:hover:not(:disabled) {
	background: transparent;
	color: var(--noc-blue, #0065ff);
	border-color: transparent;
	box-shadow: none;
	opacity: 0.82;
}

@media (max-width: 980px) {
	:deep(.creator-footer) {
		left: 16px;
		right: 16px;
		bottom: 14px;
		transform: none;
		width: auto;
		max-width: none;
		justify-content: center;
		flex-wrap: wrap;
	}

	.floating-action-btn {
		flex: 1 1 160px;
	}
}

@media (max-width: 760px) {
	:deep(.invoice-creator) {
		padding-bottom: 240px;
	}
}

.unsaved-pill {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	min-height: 32px;
	padding: 0 10px;
	border-radius: 8px;
	border: 1px solid rgba(192, 86, 33, 0.2);
	background: rgba(255, 247, 237, 0.92);
	color: #c05621;
	font-size: 0.8rem;
	font-weight: 600;
}

.inline-panel {
	display: grid;
	grid-template-columns: 1fr 240px;
	gap: 48px;
	margin-top: 32px;
}

.error-inline {
	color: var(--noc-orange, #c0392b);
	font-size: 0.85rem;
	font-weight: 600;
	align-self: center;
	max-width: 280px;
}
.helper-text {
	color: var(--text-color-secondary);
	font-size: 0.85rem;
}
.muted-helper {
	color: var(--text-color-secondary);
}
.no-building-message,
.no-selection-message {
	background: #f8fafc;
	border: 1px dashed var(--border-color-invoice-date-range, #cbd5e1);
	border-radius: 6px;
	padding: 14px 16px;
	color: var(--text-color-secondary);
	font-size: 0.9rem;
	display: block;
	width: 100%;
	box-sizing: border-box;
	margin-top: 12px;
}

/* Normalize control heights across the invoice creator */
.invoice-creator :deep(input[type='text']),
.invoice-creator :deep(input[type='date']),
.invoice-creator :deep(input[type='number']),
.invoice-creator :deep(select) {
	height: 38px;
	min-height: 38px;
	box-sizing: border-box;
	padding-top: 6px;
	padding-bottom: 6px;
}

.invoice-creator :deep(.multi-input) {
	min-height: 38px;
}

.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1200;
}

.modal-content {
	background: var(--noc-background);
	border-radius: 8px;
	border: 1px solid var(--border-color);
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
	padding: 24px;
	min-width: 320px;
	max-width: min(92vw, 560px);
	display: flex;
	flex-direction: column;
	gap: 14px;
}

.modal-title {
	margin: 0;
	font-size: 1.1rem;
	font-weight: 700;
	color: var(--text-color);
}

.modal-message {
	margin: 0;
	font-size: 0.95rem;
	color: var(--text-color-secondary);
	line-height: 1.5;
}

.modal-details {
	margin: 0;
	padding-left: 18px;
	display: flex;
	flex-direction: column;
	gap: 6px;
	color: var(--text-color);
	font-size: 0.9rem;
}

.modal-actions {
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	margin-top: 8px;
}

.modal-btn {
	padding: 0 12px;
	height: 38px;
	min-height: 38px;
	border-radius: 8px;
	border: none;
	font-weight: 500;
	cursor: pointer;
	transition: border-color 0.1s ease, background 0.15s ease, color 0.15s ease, opacity 0.15s ease;
	font-size: 0.85rem;
	box-shadow: var(--box-shadow);
}

.modal-btn.cancel {
	background: transparent;
	color: var(--text-color-secondary);
	border: 1px solid var(--border-color);
}

.modal-btn.cancel:hover:not(:disabled) {
	background: var(--noc-background-secondary);
	color: var(--text-color);
}

.modal-btn.confirm {
	background: var(--noc-blue);
	color: #fff;
}

.modal-btn.confirm:hover:not(:disabled) {
	filter: brightness(1.05);
}

.modal-btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}
</style>
